import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, PanelRightClose, PanelRightOpen, RefreshCw } from 'lucide-react';
import { CircleMarker, MapContainer, Marker, Polyline, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import LeafletTileLayer from '../../components/LeafletTileLayer';
import { clearStaffSession, isJwtExpired, resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import { buildCapacityInsights, buildCapacityRecommendations, buildOdpCapacityPredictions } from '../../utils/capacityPrediction';
import { isOperationallyValidCoordinatePair } from '../../utils/coordinateQuality';
import { AppShell, PageContainer, PageHeader, SectionContainer, UIButton, StatusBadge } from '../../features/ui-system';
import { appendTicketUpdate, createTicket, fetchTickets } from './utils/ticketingApi';
import { normalizeFtthTopologyPayload, normalizeVoucherNasPayload } from './utils/coreEndpointContract';
import { resolveCanonicalSiteIdentity } from './utils/siteIdentity';
import { deriveCustomerImpactSummary } from './utils/customerImpact';
import { deriveSiteImpactSummary } from './utils/siteImpact';
import CustomerImpactDrawer from './components/CustomerImpactDrawer';
import { createExecutionLog, fetchExecutionLogs, getExecutionActionLabel } from './utils/executionLogApi';
import {
  RUNBOOK_PRESETS,
  buildRunbookMessage,
  buildTicketShortcutNote,
  shouldMirrorRunbookToTicket,
  shouldSkipRunbookDuplicate,
} from './utils/operationalRunbook';

const API = `${resolveBackendBaseUrl()}/api`;
const DEFAULT_CENTER = [-3.4324, 119.3430];
const RELATION_TYPE_LABELS = {
  primary: 'primary',
  backup: 'backup',
  balancing: 'balancing',
  upstream: 'upstream',
  downstream: 'downstream',
  failover: 'failover',
};

const toArray = (value) => (Array.isArray(value) ? value : []);
const toRadians = (value) => (Number(value) * Math.PI) / 180;

const hasValidCoordinates = (lat, lon) => isOperationallyValidCoordinatePair(lat, lon);

const distanceMeters = (pointA, pointB) => {
  if (!Array.isArray(pointA) || !Array.isArray(pointB)) return Number.POSITIVE_INFINITY;
  const [lat1, lon1] = pointA.map(Number);
  const [lat2, lon2] = pointB.map(Number);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const earthRadius = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

const coordinateDelta = (pointA, pointB) => {
  if (!Array.isArray(pointA) || !Array.isArray(pointB)) return null;
  const latA = Number(pointA[0]);
  const lonA = Number(pointA[1]);
  const latB = Number(pointB[0]);
  const lonB = Number(pointB[1]);
  if (![latA, lonA, latB, lonB].every(Number.isFinite)) return null;
  return { lat: latA - latB, lng: lonA - lonB };
};

const normalizeKriteria = (value) => (String(value || 'live').trim().toLowerCase() === 'plan' ? 'plan' : 'live');

const resolveStatusTone = (status) => {
  const value = String(status || '').trim().toLowerCase();
  if (['offline', 'down', 'critical', 'error', 'suspended'].includes(value)) return 'danger';
  if (['degraded', 'warning', 'warn', 'flapping', 'maintenance', 'not_mapped'].includes(value)) return 'warn';
  if (['online', 'up', 'active', 'healthy'].includes(value)) return 'ok';
  return 'neutral';
};

const resolveStatusDot = (status) => {
  const tone = resolveStatusTone(status);
  if (tone === 'danger') return '#ef4444';
  if (tone === 'warn') return '#f59e0b';
  if (tone === 'ok') return '#22c55e';
  return '#94a3b8';
};

const resolveRelationTelemetryTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'active') return 'ok';
  if (token === 'degraded') return 'warn';
  return 'neutral';
};

const ticketStatusTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'closed') return 'ok';
  if (token === 'mitigated') return 'info';
  if (token === 'investigating') return 'warn';
  return 'neutral';
};

const ticketPriorityTone = (priority) => {
  const token = String(priority || '').trim().toLowerCase();
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

const executionActionTone = (actionType) => {
  const token = String(actionType || '').trim().toLowerCase();
  if (token === 'mitigation_completed') return 'ok';
  if (token === 'mitigation_started' || token === 'investigation_started' || token === 'backup_verified') return 'warn';
  if (token === 'ticket_created' || token === 'ticket_updated') return 'info';
  return 'neutral';
};

const normalizeSiteId = (...values) => {
  const identity = resolveCanonicalSiteIdentity({
    site_id: values[0],
    siteId: values[1],
    site: values[2],
    site_name: values[3],
    location: values[4],
    name: values[5],
  });
  return String(identity.site_id || '').trim();
};

const normalizeLookupToken = (value) => String(value || '').trim().toLowerCase();

const resolveRouterRoleLabel = (router = {}) => {
  const explicitRole = String(router?.router_role || router?.role || '').trim().toLowerCase();
  if (explicitRole === 'uplink') return { value: 'uplink', label: 'Uplink', source: 'explicit' };
  if (explicitRole === 'aggregation') return { value: 'aggregation', label: 'Aggregation', source: 'explicit' };
  if (explicitRole === 'load_balancer' || explicitRole === 'loadbalancer') return { value: 'load_balancer', label: 'Load Balancer', source: 'explicit' };
  if (explicitRole === 'distribution') return { value: 'distribution', label: 'Distribution', source: 'explicit' };
  if (explicitRole === 'backup') return { value: 'backup', label: 'Backup', source: 'explicit' };
  if (explicitRole === 'access') return { value: 'access', label: 'Access', source: 'explicit' };
  if (explicitRole === 'service_edge') return { value: 'service_edge', label: 'Service Edge', source: 'explicit' };

  const routerType = String(router?.router_type || router?.type || '').trim().toLowerCase();
  const identity = `${routerType} ${String(router?.primary_uplink_interface || '')}`.toLowerCase();
  if (identity.includes('uplink') || identity.includes('core') || identity.includes('gateway')) {
    return { value: 'uplink', label: 'Uplink', source: 'derived' };
  }
  if (identity.includes('agg')) {
    return { value: 'aggregation', label: 'Aggregation', source: 'derived' };
  }
  if (identity.includes('load') || identity.includes('balance') || identity.includes('lb')) {
    return { value: 'load_balancer', label: 'Load Balancer', source: 'derived' };
  }
  if (identity.includes('backup') || identity.includes('secondary')) {
    return { value: 'backup', label: 'Backup', source: 'derived' };
  }
  if (identity.includes('edge') || identity.includes('access')) {
    return { value: 'access', label: 'Access', source: 'derived' };
  }
  return { value: 'distribution', label: 'Distribution', source: 'derived' };
};

const normalizeRouterRoleToken = (value) => {
  const token = String(value || '').trim().toLowerCase();
  if (!token) return 'distribution';
  if (token === 'loadbalancer') return 'load_balancer';
  return token;
};

const formatAge = (timestamp) => {
  const parsed = new Date(timestamp || '');
  if (Number.isNaN(parsed.getTime())) return '-';
  const diffSec = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}d`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}j`;
  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}h`;
};

const createIncidentIcon = (severity = 'high') => {
  const color = resolveStatusDot(severity);
  return L.divIcon({
    className: 'topology-incident-icon',
    html: `<span style="display:inline-flex;width:18px;height:18px;border-radius:999px;background:${color};box-shadow:0 0 0 3px rgba(2,6,23,0.8),0 0 18px ${color};"></span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -12],
  });
};

function LeafletMapReadyBridge({ onReady, debugEnabled }) {
  const map = useMap();
  useEffect(() => {
    if (typeof onReady === 'function') onReady(map);
  }, [map, onReady]);

  useMapEvents({
    moveend: () => {
      if (!debugEnabled) return;
      try {
        const center = map.getCenter();
        console.debug('[topology][focus] moveend', {
          center: [center.lat, center.lng],
          zoom: map.getZoom(),
        });
      } catch (_err) {
        // debug only
      }
    },
  });

  return null;
}

function TopologyWorkspaceUnified() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [olts, setOlts] = useState([]);
  const [odps, setOdps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [nasRouters, setNasRouters] = useState([]);
  const [sites, setSites] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');

  const [mode, setMode] = useState('live');
  const [infraScope, setInfraScope] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [isIncidentQueueExpanded, setIsIncidentQueueExpanded] = useState(true);
  const [showFilterRail, setShowFilterRail] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [layers, setLayers] = useState({ customer: true, odp: true, olt: true, incident: true, links: true });
  const [selectedEntityKey, setSelectedEntityKey] = useState('');
  const [selectedSiteKey, setSelectedSiteKey] = useState('');
  const [isSiteInspectorExpanded, setIsSiteInspectorExpanded] = useState(true);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [pendingDeepLink, setPendingDeepLink] = useState(null);
  const [pendingViewportFocus, setPendingViewportFocus] = useState(null);
  const [pendingRouterDeepLinkFocus, setPendingRouterDeepLinkFocus] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);
  const viewportFocusLockRef = useRef({ active: false, reason: '', expiresAt: 0 });
  const [routerCatalog, setRouterCatalog] = useState([]);
  const [routerCatalogReady, setRouterCatalogReady] = useState(false);
  const [routerRelations, setRouterRelations] = useState([]);
  const [routerRelationsLoading, setRouterRelationsLoading] = useState(false);
  const [routerRelationsError, setRouterRelationsError] = useState('');
  const [relatedTickets, setRelatedTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState('');
  const [ticketDraft, setTicketDraft] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
  });
  const [ticketUpdateNote, setTicketUpdateNote] = useState('');
  const [ticketSaving, setTicketSaving] = useState(false);
  const [showAllRelatedTickets, setShowAllRelatedTickets] = useState(false);
  const [showAllTicketLogs, setShowAllTicketLogs] = useState(false);
  const [showImpactDrawer, setShowImpactDrawer] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionLogsLoading, setExecutionLogsLoading] = useState(false);
  const [executionLogsError, setExecutionLogsError] = useState('');
  const [showAllExecutionLogs, setShowAllExecutionLogs] = useState(false);
  const [executionNote, setExecutionNote] = useState('');
  const deepLinkFocusConsumedRef = useRef(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    if (!token || isJwtExpired(token)) {
      clearStaffSession();
      navigate('/dadu/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const isDeepLinkDebugEnabled = useCallback(() => (
    String(new URLSearchParams(location.search || '').get('dlDebug') || '').trim() === '1'
  ), [location.search]);

  const focusLeafletMapToCoordinates = useCallback((latitude, longitude, zoom = 15, reason = 'unknown') => {
    if (!mapRef.current || !isMapReady) return false;
    if (!hasValidCoordinates(latitude, longitude)) return false;
    const coords = [Number(latitude), Number(longitude)];
    const mapCenterBefore = mapRef.current.getCenter ? mapRef.current.getCenter() : null;
    const beforeCenter = mapCenterBefore ? [mapCenterBefore.lat, mapCenterBefore.lng] : null;
    const beforeZoom = mapRef.current.getZoom ? mapRef.current.getZoom() : null;
    if (isDeepLinkDebugEnabled()) {
      console.debug('[topology][focus] before center', { reason, center: beforeCenter, zoom: beforeZoom });
      console.debug('[topology][focus] setView', { reason, coordinates: coords, zoom });
    }
    mapRef.current.invalidateSize();
    mapRef.current.setView(coords, Number(zoom), { animate: false });
    if (isDeepLinkDebugEnabled()) {
      const afterSetViewCenter = mapRef.current.getCenter ? mapRef.current.getCenter() : null;
      const afterSetViewZoom = mapRef.current.getZoom ? mapRef.current.getZoom() : null;
      console.debug('[topology][focus] after setView', {
        reason,
        center: afterSetViewCenter ? [afterSetViewCenter.lat, afterSetViewCenter.lng] : null,
        zoom: afterSetViewZoom,
      });
      console.debug('[topology][focus] flyTo', { reason, coordinates: coords, zoom });
    }
    if (isDeepLinkDebugEnabled()) {
      const afterFlyToCallCenter = mapRef.current.getCenter ? mapRef.current.getCenter() : null;
      const afterFlyToCallZoom = mapRef.current.getZoom ? mapRef.current.getZoom() : null;
      console.debug('[topology][focus] after flyTo', {
        reason,
        center: afterFlyToCallCenter ? [afterFlyToCallCenter.lat, afterFlyToCallCenter.lng] : null,
        zoom: afterFlyToCallZoom,
      });
    }
    mapRef.current.flyTo(coords, Number(zoom), { duration: 0.55 });
    return true;
  }, [isMapReady, isDeepLinkDebugEnabled]);

  const onLeafletMapReady = useCallback((instance) => {
    if (!instance) return;
    mapRef.current = instance;
    setIsMapReady(true);
    if (typeof window !== 'undefined') {
      window.__topologyLeafletMap = instance;
    }
    if (isDeepLinkDebugEnabled()) {
      console.debug('[topology][focus] map ready', {
        center: instance.getCenter ? [instance.getCenter().lat, instance.getCenter().lng] : null,
        zoom: instance.getZoom ? instance.getZoom() : null,
      });
    }
  }, [isDeepLinkDebugEnabled]);

  const fetchTopology = useCallback(async () => {
    const headers = getHeaders();
    if (!headers) return;
    setLoading(true);
    try {
      const [topologyRes, statusRes, nasRes, sitesRes] = await Promise.allSettled([
        fetch(`${API}/ftth/topology`, { headers }).then((res) => res.json()),
        fetch(`${API}/genieacs/customer-status`, { headers }).then((res) => res.json()),
        fetch(`${API}/voucher/nas`, { headers }).then((res) => res.json()),
        fetch(`${API}/sites`, { headers }).then((res) => res.json()),
      ]);

      if (topologyRes.status !== 'fulfilled') throw new Error('Tidak bisa mengambil topologi.');

      const topology = normalizeFtthTopologyPayload(topologyRes.value || {});
      const statusPayload = statusRes.status === 'fulfilled' ? (statusRes.value || {}) : {};
      const nasPayload = normalizeVoucherNasPayload(nasRes.status === 'fulfilled' ? (nasRes.value || {}) : {});
      const sitePayload = sitesRes.status === 'fulfilled' ? (sitesRes.value || []) : [];
      const statusMap = {};
      toArray(statusPayload.statuses).forEach((item) => {
        if (item?.customer_id) statusMap[String(item.customer_id)] = item;
      });

      const nextCustomers = toArray(topology.customers).map((item) => {
        const statusDetail = statusMap[String(item?.id || '')] || null;
        return {
          ...item,
          linked_odp_id: item?.linked_odp_id || item?.odp_id || null,
          linked_olt_id: item?.linked_olt_id || item?.olt_id || null,
          status: statusDetail?.connection_status || item?.status || item?.connection_status || 'unknown',
          status_detail: statusDetail || item?.status_detail || null,
        };
      });

      setOlts(toArray(topology.olts));
      setOdps(toArray(topology.odps));
      setCustomers(nextCustomers);
      setNasRouters(toArray(nasPayload.items));
      setSites(toArray(sitePayload));
      setLastUpdated(statusPayload.updated_at || new Date().toISOString());
    } catch (error) {
      toast.error(error?.message || 'Gagal memuat workspace topologi.');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchTopology();
  }, [fetchTopology]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const queryMode = String(params.get('mode') || '').trim().toLowerCase();
    setMode(queryMode === 'plan' ? 'plan' : 'live');
    const sourceType = String(params.get('sourceType') || '').trim().toLowerCase();
    const sourceId = String(params.get('sourceId') || '').trim();
    const latitude = String(params.get('latitude') || '').trim();
    const longitude = String(params.get('longitude') || '').trim();
    setPendingDeepLink({
      sourceType,
      sourceId,
      routerId: String(params.get('routerId') || '').trim(),
      siteId: String(params.get('siteId') || params.get('site') || '').trim(),
      openDetail: String(params.get('openDetail') || '').trim() === '1',
      latitude,
      longitude,
    });
  }, [location.search]);

  useEffect(() => {
    setIsDetailPanelOpen(mode === 'plan');
    setShowFilterRail(mode === 'plan');
    if (mode === 'live') {
      setShowTimeline(false);
      setTimelineExpanded(false);
    }
  }, [mode]);

  const updateMode = (nextMode) => {
    const params = new URLSearchParams(location.search || '');
    params.set('mode', nextMode);
    navigate(`/topology?${params.toString()}`, { replace: true });
  };

  const allOltsById = useMemo(() => {
    const mapped = {};
    olts.forEach((item) => { if (item?.id) mapped[String(item.id)] = item; });
    return mapped;
  }, [olts]);

  const filteredOlts = useMemo(() => olts.filter((item) => {
    const category = normalizeKriteria(item?.kriteria);
    if (infraScope !== 'all' && category !== infraScope) return false;
    if (siteFilter !== 'all' && normalizeSiteId(item?.site_id, item?.location, item?.site) !== siteFilter) return false;
    return true;
  }), [olts, infraScope, siteFilter]);

  const filteredOltsById = useMemo(() => {
    const mapped = {};
    filteredOlts.forEach((item) => { if (item?.id) mapped[String(item.id)] = item; });
    return mapped;
  }, [filteredOlts]);

  const filteredOdps = useMemo(() => odps.filter((item) => {
    const category = normalizeKriteria(item?.kriteria);
    if (infraScope !== 'all' && category !== infraScope) return false;
    const linkedOlt = allOltsById[String(item?.olt_id || '')];
    const site = normalizeSiteId(item?.site_id, linkedOlt?.site_id, linkedOlt?.location, linkedOlt?.site, item?.site);
    if (siteFilter !== 'all' && site !== siteFilter) return false;
    if (item?.olt_id && !filteredOltsById[String(item.olt_id)]) return false;
    return true;
  }), [odps, allOltsById, filteredOltsById, infraScope, siteFilter]);

  const filteredOdpsById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((item) => { if (item?.id) mapped[String(item.id)] = item; });
    return mapped;
  }, [filteredOdps]);

  const customersForView = useMemo(() => customers.filter((customer) => {
    const odpId = String(customer?.linked_odp_id || customer?.odp_id || '').trim();
    const linkedOdp = odpId ? filteredOdpsById[odpId] : null;
    if (infraScope !== 'all' && !linkedOdp) return false;
    if (siteFilter !== 'all') {
      const linkedOlt = linkedOdp ? allOltsById[String(linkedOdp.olt_id || '')] : null;
      const site = normalizeSiteId(
        customer?.site_id,
        customer?.siteId,
        linkedOdp?.site_id,
        linkedOlt?.site_id,
        linkedOlt?.location,
        linkedOlt?.site,
      );
      if (site !== siteFilter) return false;
    }
    return true;
  }), [customers, filteredOdpsById, allOltsById, infraScope, siteFilter]);

  const mapCustomers = useMemo(() => customersForView.filter((item) => hasValidCoordinates(item?.latitude, item?.longitude)), [customersForView]);

  const capacityPrediction = useMemo(() => {
    const odpPredictions = buildOdpCapacityPredictions({
      odps: filteredOdps,
      customers: customersForView,
      resolveCustomerOdpId: (customer) => customer?.linked_odp_id || customer?.odp_id,
    });
    const recommendations = buildCapacityRecommendations({ odpPredictions, strategy: 'balanced', limit: 8 });
    const insights = buildCapacityInsights({ odpPredictions, nasPredictions: [] });
    return {
      odps: odpPredictions,
      recommendations: recommendations.recommendations || [],
      insights,
      byOdpId: odpPredictions.reduce((acc, item) => { acc[String(item.id)] = item; return acc; }, {}),
    };
  }, [customersForView, filteredOdps]);

  const generatedIncidents = useMemo(() => {
    const incidents = [];
    mapCustomers.forEach((customer) => {
      const status = String(customer?.status || '').toLowerCase();
      if (status === 'offline' || status === 'down') {
        incidents.push({
          id: `inc-cust-${customer.id}`,
          severity: 'critical',
          title: `Pelanggan offline: ${customer.name}`,
          subtitle: `${customer.customer_id || customer.id} - perlu investigasi akses`,
          linkedEntityKeys: [`customer:${customer.id}`],
          coordinates: [Number(customer.latitude), Number(customer.longitude)],
          timestamp: customer?.status_detail?.last_inform || new Date().toISOString(),
        });
      }
    });

    filteredOdps.forEach((odp) => {
      const prediction = capacityPrediction.byOdpId[String(odp.id)];
      if (!prediction) return;
      if (prediction.risk?.level === 'critical' || prediction.risk?.level === 'high') {
        incidents.push({
          id: `inc-odp-${odp.id}`,
          severity: prediction.risk.level === 'critical' ? 'critical' : 'high',
          title: `Risiko kapasitas ${odp.name}`,
          subtitle: `${prediction.used}/${prediction.capacity} terpakai - ${prediction.risk.label}`,
          linkedEntityKeys: [`odp:${odp.id}`],
          coordinates: hasValidCoordinates(odp.latitude, odp.longitude) ? [Number(odp.latitude), Number(odp.longitude)] : null,
          timestamp: new Date().toISOString(),
        });
      }
    });

    return incidents;
  }, [mapCustomers, filteredOdps, capacityPrediction.byOdpId]);

  useEffect(() => {
    if (mode !== 'live') return;
    setIsIncidentQueueExpanded(generatedIncidents.length > 0);
  }, [mode, generatedIncidents.length]);

  const siteOptions = useMemo(() => {
    const set = new Set();
    sites.forEach((item) => {
      const site = normalizeSiteId(item?.id, item?.code, item?.name);
      if (site) set.add(site);
    });
    olts.forEach((item) => {
      const site = normalizeSiteId(item?.site_id, item?.location, item?.site);
      if (site) set.add(site);
    });
    odps.forEach((item) => {
      const site = normalizeSiteId(item?.site_id, item?.location, item?.site);
      if (site) set.add(site);
    });
    nasRouters.forEach((item) => {
      const site = normalizeSiteId(item?.site_id, item?.site_name, item?.site);
      if (site) set.add(site);
    });
    customers.forEach((item) => {
      const site = normalizeSiteId(item?.site_id, item?.siteId, item?.site);
      if (site) set.add(site);
    });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [sites, olts, odps, nasRouters, customers]);

  const routerCatalogIndex = useMemo(() => {
    const index = new Map();
    routerCatalog.forEach((router) => {
      const primaryId = String(router?.id || '').trim();
      const candidates = new Set(
        [
          primaryId,
          router?.nas_identifier,
          router?.identity,
          router?.name,
          router?.router_label,
        ]
          .map(normalizeLookupToken)
          .filter(Boolean),
      );
      candidates.forEach((key) => {
        if (!index.has(key)) index.set(key, router);
      });
    });
    return index;
  }, [routerCatalog]);

  const siteCatalogById = useMemo(() => {
    const mapped = new Map();
    sites.forEach((site) => {
      const siteId = String(site?.id || '').trim();
      if (!siteId) return;
      mapped.set(siteId, site);
      const code = String(site?.code || '').trim();
      if (code && !mapped.has(code)) mapped.set(code, site);
    });
    return mapped;
  }, [sites]);

  const workspaceEntities = useMemo(() => {
    const entities = [];
    nasRouters.forEach((item) => {
      const matchedRouter = (
        routerCatalogIndex.get(normalizeLookupToken(item?.router_id))
        || routerCatalogIndex.get(normalizeLookupToken(item?.id))
        || routerCatalogIndex.get(normalizeLookupToken(item?.nas_identifier))
        || routerCatalogIndex.get(normalizeLookupToken(item?.name))
      );
      const routerRole = resolveRouterRoleLabel({ ...(matchedRouter || {}), ...item });
      const siteId = normalizeSiteId(item?.site_id, matchedRouter?.site_id, item?.site_name, item?.site);
      const latitude = hasValidCoordinates(item?.latitude, item?.longitude)
        ? Number(item.latitude)
        : hasValidCoordinates(matchedRouter?.latitude, matchedRouter?.longitude)
          ? Number(matchedRouter.latitude)
          : null;
      const longitude = hasValidCoordinates(item?.latitude, item?.longitude)
        ? Number(item.longitude)
        : hasValidCoordinates(matchedRouter?.latitude, matchedRouter?.longitude)
          ? Number(matchedRouter.longitude)
          : null;
      entities.push({
        key: `nas:${item.id || item.router_id}`,
        type: 'nas',
        id: item.id || item.router_id,
        name: item.name || item.nas_identifier || `NAS ${item.id || item.router_id || '-'}`,
        subtitle: `${siteId || '-'} - ${routerRole.label}`,
        status: item.status || item.connection_status || 'unknown',
        coordinates: Number.isFinite(latitude) && Number.isFinite(longitude) ? [latitude, longitude] : null,
        siteId,
        routerRole,
        data: {
          ...item,
          router_id: item?.router_id || matchedRouter?.id || null,
          latitude: Number.isFinite(latitude) ? latitude : item?.latitude,
          longitude: Number.isFinite(longitude) ? longitude : item?.longitude,
          site_id: siteId || item?.site_id || matchedRouter?.site_id || null,
        },
      });
    });
    filteredOlts.forEach((item) => {
      const siteId = normalizeSiteId(item?.site_id, item?.location, item?.site);
      entities.push({
        key: `olt:${item.id}`,
        type: 'olt',
        id: item.id,
        name: item.name,
        subtitle: `${item.code || '-'} - ${normalizeKriteria(item.kriteria).toUpperCase()}`,
        status: normalizeKriteria(item.kriteria) === 'plan' ? 'planning' : 'online',
        coordinates: hasValidCoordinates(item.latitude, item.longitude) ? [Number(item.latitude), Number(item.longitude)] : null,
        siteId,
        data: item,
      });
    });
    filteredOdps.forEach((item) => {
      const prediction = capacityPrediction.byOdpId[String(item.id)] || null;
      const status = prediction?.risk?.level === 'critical' ? 'critical' : prediction?.risk?.level === 'high' ? 'degraded' : normalizeKriteria(item.kriteria) === 'plan' ? 'planning' : 'online';
      const linkedOlt = allOltsById[String(item?.olt_id || '')];
      const siteId = normalizeSiteId(item?.site_id, linkedOlt?.site_id, linkedOlt?.location, linkedOlt?.site);
      entities.push({
        key: `odp:${item.id}`,
        type: 'odp',
        id: item.id,
        name: item.name,
        subtitle: `${item.code || '-'} - OLT ${item.olt_id || '-'}`,
        status,
        coordinates: hasValidCoordinates(item.latitude, item.longitude) ? [Number(item.latitude), Number(item.longitude)] : null,
        siteId,
        data: item,
      });
    });
    mapCustomers.forEach((item) => {
      const linkedOdp = filteredOdpsById[String(item?.linked_odp_id || item?.odp_id || '')];
      const linkedOlt = allOltsById[String(linkedOdp?.olt_id || item?.linked_olt_id || item?.olt_id || '')];
      const siteId = normalizeSiteId(item?.site_id, item?.siteId, linkedOdp?.site_id, linkedOlt?.site_id, linkedOlt?.location, linkedOlt?.site);
      entities.push({
        key: `customer:${item.id}`,
        type: 'customer',
        id: item.id,
        name: item.name,
        subtitle: `${item.customer_id || item.id} - ${item.linked_odp_id || item.odp_id || 'unmapped'}`,
        status: item.status || 'unknown',
        coordinates: [Number(item.latitude), Number(item.longitude)],
        siteId,
        data: item,
      });
    });
    generatedIncidents.forEach((item) => entities.push({
      key: `incident:${item.id}`,
      type: 'incident',
      id: item.id,
      name: item.title,
      subtitle: item.subtitle,
      status: item.severity,
      coordinates: item.coordinates,
      siteId: '',
      data: item,
    }));
    return entities;
  }, [nasRouters, routerCatalogIndex, filteredOlts, filteredOdps, mapCustomers, generatedIncidents, capacityPrediction.byOdpId, allOltsById, filteredOdpsById]);

  const layerFilteredEntities = useMemo(() => workspaceEntities.filter((entity) => {
    const keyword = String(searchQuery || '').trim().toLowerCase();
    if (keyword) {
      const haystack = `${entity.name || ''} ${entity.subtitle || ''} ${entity.type || ''}`.toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    if (!layers.customer && entity.type === 'customer') return false;
    if (!layers.odp && entity.type === 'odp') return false;
    if (!layers.olt && entity.type === 'olt') return false;
    if (!layers.incident && entity.type === 'incident') return false;
    if (mode === 'live' && entity.type === 'odp' && String(entity.status) === 'planning') return false;
    if (mode === 'plan' && entity.type === 'incident' && String(entity.status) === 'critical') return false;
    return true;
  }), [workspaceEntities, layers, mode, searchQuery]);

  const incidentQueue = useMemo(() => generatedIncidents.slice().sort((a, b) => (a.severity === 'critical' ? -1 : 1) - (b.severity === 'critical' ? -1 : 1)), [generatedIncidents]);

  const siteGroups = useMemo(() => {
    const grouped = {};
    const nonIncidentEntities = layerFilteredEntities.filter((entity) => entity.type !== 'incident');
    nonIncidentEntities.forEach((entity) => {
      const siteId = normalizeSiteId(entity.siteId, entity?.data?.site_id, entity?.data?.site_name, entity?.data?.site);
      const siteKey = siteId || 'site:unassigned';
      if (!grouped[siteKey]) {
        grouped[siteKey] = {
          key: siteKey,
          siteId: siteId || '',
          name: siteId || 'Belum terpetakan',
          nodes: [],
          coordinatesList: [],
          counts: { nas: 0, olt: 0, odp: 0, customer: 0 },
          tones: [],
        };
      }
      grouped[siteKey].nodes.push(entity);
      grouped[siteKey].counts[entity.type] = (grouped[siteKey].counts[entity.type] || 0) + 1;
      grouped[siteKey].tones.push(resolveStatusTone(entity.status));
      if (Array.isArray(entity.coordinates)) grouped[siteKey].coordinatesList.push(entity.coordinates);
    });

    const incidentByEntityKey = {};
    incidentQueue.forEach((incident) => {
      toArray(incident?.linkedEntityKeys).forEach((key) => {
        const clean = String(key || '').trim();
        if (!clean) return;
        incidentByEntityKey[clean] = (incidentByEntityKey[clean] || 0) + 1;
      });
    });

    return Object.values(grouped)
      .map((site) => {
        const catalogSite = siteCatalogById.get(String(site.siteId || '').trim());
        const catalogCoordinates = hasValidCoordinates(catalogSite?.latitude, catalogSite?.longitude)
          ? [Number(catalogSite.latitude), Number(catalogSite.longitude)]
          : null;
        const coordinates = site.coordinatesList.length
          ? [
            site.coordinatesList.reduce((acc, pair) => acc + Number(pair[0] || 0), 0) / site.coordinatesList.length,
            site.coordinatesList.reduce((acc, pair) => acc + Number(pair[1] || 0), 0) / site.coordinatesList.length,
          ]
          : catalogCoordinates;
        const incidentCount = site.nodes.reduce((acc, node) => acc + (incidentByEntityKey[node.key] || 0), 0);
        const tone = site.tones.includes('danger') ? 'danger'
          : site.tones.includes('warn') ? 'warn'
            : site.tones.includes('ok') ? 'ok'
              : 'neutral';
        return {
          ...site,
          coordinates,
          incidentCount,
          tone,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [layerFilteredEntities, incidentQueue, siteCatalogById]);

  const selectedSite = useMemo(() => siteGroups.find((site) => site.key === selectedSiteKey) || null, [siteGroups, selectedSiteKey]);

  useEffect(() => {
    if (!pendingDeepLink) return;
    if (!routerCatalogReady) return;
    if (!layerFilteredEntities.length && !siteGroups.length) return;

    const deepLink = pendingDeepLink;
    const sourceType = String(deepLink?.sourceType || '').trim().toLowerCase();
    const sourceId = String(deepLink?.sourceId || '').trim();
    const routerId = String(deepLink?.routerId || '').trim();
    const siteId = String(deepLink?.siteId || '').trim();
    const shouldOpenDetail = Boolean(deepLink?.openDetail);
    const queryLatitude = String(deepLink?.latitude || '').trim();
    const queryLongitude = String(deepLink?.longitude || '').trim();
    const debugDeepLink = isDeepLinkDebugEnabled();

    const siteMatch = siteGroups.find((site) => (
      String(site.key || '').trim().toLowerCase() === siteId.toLowerCase()
      || String(site.siteId || '').trim().toLowerCase() === siteId.toLowerCase()
    ));
    if (siteMatch) {
      setSelectedSiteKey(siteMatch.key);
    }

    let matchedEntity = null;
    const allCandidates = new Set(
      [sourceId, routerId]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean),
    );

    if (sourceType === 'nas' || routerId) {
      matchedEntity = layerFilteredEntities.find((entity) => {
        if (entity.type !== 'nas') return false;
        const entityCandidates = [
          entity.id,
          entity?.data?.id,
          entity?.data?.router_id,
          entity?.data?.nas_identifier,
        ]
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean);
        return entityCandidates.some((token) => allCandidates.has(token));
      }) || null;
    }

    if (!matchedEntity && sourceType && sourceId) {
      matchedEntity = layerFilteredEntities.find((entity) => (
        String(entity.type || '').trim().toLowerCase() === sourceType
        && String(entity.id || '').trim() === sourceId
      )) || null;
    }

    if (!matchedEntity && sourceType && sourceId) {
      const targetKey = `${sourceType}:${sourceId}`;
      matchedEntity = layerFilteredEntities.find((entity) => String(entity.key || '') === targetKey) || null;
    }

    const resolveNasDeepLinkCoordinates = () => {
      if (hasValidCoordinates(queryLatitude, queryLongitude)) {
        return [Number(queryLatitude), Number(queryLongitude)];
      }
      const candidateTokens = new Set(
        [
          routerId,
          sourceId,
          matchedEntity?.id,
          matchedEntity?.data?.id,
          matchedEntity?.data?.router_id,
          matchedEntity?.data?.nas_identifier,
        ]
          .map(normalizeLookupToken)
          .filter(Boolean),
      );
      if (!candidateTokens.size) return null;
      const matchedRouterFromCatalog = routerCatalog.find((router) => {
        const routerTokens = [
          router?.id,
          router?.nas_identifier,
          router?.identity,
          router?.name,
          router?.router_label,
        ]
          .map(normalizeLookupToken)
          .filter(Boolean);
        return routerTokens.some((token) => candidateTokens.has(token));
      });
      if (hasValidCoordinates(matchedRouterFromCatalog?.latitude, matchedRouterFromCatalog?.longitude)) {
        return [Number(matchedRouterFromCatalog.latitude), Number(matchedRouterFromCatalog.longitude)];
      }
      if (matchedEntity?.type === 'nas' && Array.isArray(matchedEntity.coordinates)) {
        return matchedEntity.coordinates;
      }
      return null;
    };

    if (matchedEntity) {
      setSelectedEntityKey(matchedEntity.key);
      if (shouldOpenDetail) setIsDetailPanelOpen(true);
      const entitySiteId = normalizeSiteId(
        matchedEntity.siteId,
        matchedEntity?.data?.site_id,
        matchedEntity?.data?.site_name,
        matchedEntity?.data?.site,
      );
      if (entitySiteId) setSelectedSiteKey(entitySiteId);
      if (matchedEntity.type === 'nas') {
        const routerFocusCoordinates = resolveNasDeepLinkCoordinates();
        if (debugDeepLink) {
          console.debug('[topology][deep-link] nas resolve', {
            sourceId,
            routerId,
            matchedEntityKey: matchedEntity.key,
            entityCoordinates: matchedEntity.coordinates,
            queryLatitude,
            queryLongitude,
            resolvedRouterCoordinates: routerFocusCoordinates,
          });
        }
        if (Array.isArray(routerFocusCoordinates)) {
          if (debugDeepLink) {
            console.debug('[topology][focus] router coordinates', {
              selectedRouterKey: matchedEntity.key,
              coordinates: routerFocusCoordinates,
              source: hasValidCoordinates(queryLatitude, queryLongitude) ? 'query' : 'router_catalog_or_entity',
            });
          }
          viewportFocusLockRef.current = { active: true, reason: 'deep_link_router', expiresAt: Date.now() + 4500 };
          setPendingRouterDeepLinkFocus({
            targetEntityKey: matchedEntity.key,
            coordinates: routerFocusCoordinates,
            zoom: 16,
            reason: 'deep_link_router',
          });
          setPendingDeepLink(null);
          return;
        }
      }
      if (Array.isArray(matchedEntity.coordinates)) {
        const zoom = matchedEntity.type === 'nas' ? 16 : 15;
        setPendingViewportFocus({ coordinates: matchedEntity.coordinates, zoom, reason: 'deep_link_entity' });
      } else if (hasValidCoordinates(queryLatitude, queryLongitude)) {
        const fallbackCoordinates = [Number(queryLatitude), Number(queryLongitude)];
        viewportFocusLockRef.current = { active: true, reason: 'deep_link_router_fallback_query', expiresAt: Date.now() + 4500 };
        setPendingRouterDeepLinkFocus({
          targetEntityKey: matchedEntity.key,
          coordinates: fallbackCoordinates,
          zoom: 16,
          reason: 'deep_link_router_fallback_query',
        });
        setPendingViewportFocus({
          coordinates: fallbackCoordinates,
          zoom: matchedEntity.type === 'nas' ? 16 : 15,
          reason: 'deep_link_router_fallback_query',
        });
      } else if (Array.isArray(siteMatch?.coordinates)) {
        setPendingViewportFocus({ coordinates: siteMatch.coordinates, zoom: 14, reason: 'deep_link_site_fallback' });
      }
    } else if (hasValidCoordinates(queryLatitude, queryLongitude)) {
      setPendingViewportFocus({ coordinates: [Number(queryLatitude), Number(queryLongitude)], zoom: 15, reason: 'deep_link_query_only' });
    } else if (Array.isArray(siteMatch?.coordinates)) {
      setPendingViewportFocus({ coordinates: siteMatch.coordinates, zoom: 14, reason: 'deep_link_site_only' });
    }

    setPendingDeepLink(null);
  }, [pendingDeepLink, layerFilteredEntities, siteGroups, routerCatalogReady, routerCatalog, location.search, isDeepLinkDebugEnabled]);

  useEffect(() => {
    if (!pendingViewportFocus || !isMapReady || !mapRef.current) return;
    const reason = String(pendingViewportFocus?.reason || '').trim();
    const lockIsActive = viewportFocusLockRef.current.active && Number(viewportFocusLockRef.current.expiresAt || 0) > Date.now();
    if (lockIsActive && viewportFocusLockRef.current.reason === 'deep_link_router' && reason && reason !== 'deep_link_router') {
      if (isDeepLinkDebugEnabled()) {
        console.debug('[topology][focus] overwrite attempt', {
          incomingReason: reason,
          lockReason: viewportFocusLockRef.current.reason,
        });
        console.debug('[topology][focus] overwrite blocked', {
          incomingReason: reason,
          lockReason: viewportFocusLockRef.current.reason,
        });
      }
      setPendingViewportFocus(null);
      return;
    }
    const coordinates = pendingViewportFocus.coordinates;
    if (Array.isArray(coordinates) && coordinates.length === 2) {
      focusLeafletMapToCoordinates(Number(coordinates[0]), Number(coordinates[1]), Number(pendingViewportFocus.zoom || 15), reason || 'pending_viewport_focus');
    }
    setPendingViewportFocus(null);
  }, [pendingViewportFocus, isMapReady, focusLeafletMapToCoordinates, isDeepLinkDebugEnabled]);

  useEffect(() => {
    if (!pendingRouterDeepLinkFocus || !isMapReady || !mapRef.current) return;
    if (deepLinkFocusConsumedRef.current) return;
    const coordinates = pendingRouterDeepLinkFocus.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length !== 2) {
      setPendingRouterDeepLinkFocus(null);
      return;
    }
    const targetEntityKey = String(pendingRouterDeepLinkFocus.targetEntityKey || '').trim();
    if (targetEntityKey && selectedEntityKey && targetEntityKey !== selectedEntityKey) {
      return;
    }
    const targetEntity = layerFilteredEntities.find((entity) => String(entity?.key || '') === targetEntityKey) || null;
    const entityCoordinates = Array.isArray(targetEntity?.coordinates) && targetEntity.coordinates.length === 2
      ? [Number(targetEntity.coordinates[0]), Number(targetEntity.coordinates[1])]
      : null;
    const hasValidEntityCoordinates = Array.isArray(entityCoordinates) && hasValidCoordinates(entityCoordinates[0], entityCoordinates[1]);
    const resolvedCoordinates = hasValidEntityCoordinates
      ? entityCoordinates
      : [Number(coordinates[0]), Number(coordinates[1])];
    if (!hasValidCoordinates(resolvedCoordinates[0], resolvedCoordinates[1])) {
      setPendingRouterDeepLinkFocus(null);
      return;
    }
    const zoom = Number(pendingRouterDeepLinkFocus.zoom || 16);
    if (isDeepLinkDebugEnabled()) {
      const delta = entityCoordinates ? coordinateDelta(entityCoordinates, coordinates) : null;
      console.debug('[topology][focus] router target', {
        targetEntityKey,
        selectedEntityKey,
        coordinates: resolvedCoordinates,
        queryOrCatalogCoordinates: coordinates,
        entityCoordinates,
        coordinateDelta: delta,
        zoom,
        reason: pendingRouterDeepLinkFocus.reason || 'deep_link_router',
      });
      console.debug('[topology][focus] router coords used for focus', {
        coordinates: resolvedCoordinates,
        source: hasValidEntityCoordinates ? 'selected_entity' : 'pending_deep_link',
      });
    }
    viewportFocusLockRef.current = { active: true, reason: 'deep_link_router', expiresAt: Date.now() + 4500 };
    deepLinkFocusConsumedRef.current = true;
    focusLeafletMapToCoordinates(
      Number(resolvedCoordinates[0]),
      Number(resolvedCoordinates[1]),
      zoom,
      String(pendingRouterDeepLinkFocus.reason || 'deep_link_router'),
    );
    window.setTimeout(() => {
      if (!mapRef.current) return;
      const currentCenter = mapRef.current.getCenter ? mapRef.current.getCenter() : null;
      if (!currentCenter) return;
      const currentPair = [Number(currentCenter.lat), Number(currentCenter.lng)];
      const delta = coordinateDelta(currentPair, resolvedCoordinates);
      if (isDeepLinkDebugEnabled()) {
        console.debug('[topology][focus] final map center', {
          center: currentPair,
          expected: resolvedCoordinates,
          delta,
        });
      }
      const isOffTarget = !delta || Math.abs(delta.lat) > 0.00001 || Math.abs(delta.lng) > 0.00001;
      if (isOffTarget) {
        if (isDeepLinkDebugEnabled()) {
          console.debug('[topology][focus] corrective setView', {
            reason: 'deep_link_router_corrective',
            coordinates: resolvedCoordinates,
            zoom,
          });
        }
        mapRef.current.setView(resolvedCoordinates, zoom, { animate: false });
      }
      if (isDeepLinkDebugEnabled()) {
        const finalCenter = mapRef.current.getCenter ? mapRef.current.getCenter() : null;
        console.debug('[topology][focus] final map center after corrective', {
          center: finalCenter ? [finalCenter.lat, finalCenter.lng] : null,
          expected: resolvedCoordinates,
          delta: finalCenter ? coordinateDelta([finalCenter.lat, finalCenter.lng], resolvedCoordinates) : null,
        });
      }
    }, 700);
    setTimeout(() => {
      if (viewportFocusLockRef.current.reason === 'deep_link_router') {
        viewportFocusLockRef.current = { active: false, reason: '', expiresAt: 0 };
      }
      deepLinkFocusConsumedRef.current = false;
    }, 1800);
    setPendingRouterDeepLinkFocus(null);
  }, [pendingRouterDeepLinkFocus, isMapReady, selectedEntityKey, layerFilteredEntities, focusLeafletMapToCoordinates, isDeepLinkDebugEnabled]);

  useEffect(() => {
    if (!layerFilteredEntities.length) {
      setSelectedEntityKey('');
      return;
    }
    if (selectedEntityKey && !layerFilteredEntities.some((item) => item.key === selectedEntityKey)) {
      setSelectedEntityKey('');
    }
  }, [layerFilteredEntities, selectedEntityKey]);

  useEffect(() => {
    if (!siteGroups.length) {
      setSelectedSiteKey('');
      return;
    }
    if (!siteGroups.some((site) => site.key === selectedSiteKey)) {
      setSelectedSiteKey(siteGroups[0].key);
    }
  }, [siteGroups, selectedSiteKey]);

  const selectedEntity = useMemo(() => layerFilteredEntities.find((item) => item.key === selectedEntityKey) || null, [layerFilteredEntities, selectedEntityKey]);

  useEffect(() => {
    if (!selectedEntity) return;
    const entitySiteId = normalizeSiteId(selectedEntity.siteId, selectedEntity?.data?.site_id, selectedEntity?.data?.site_name, selectedEntity?.data?.site);
    const nextSiteKey = entitySiteId || 'site:unassigned';
    setSelectedSiteKey((prev) => (prev === nextSiteKey ? prev : nextSiteKey));
  }, [selectedEntity]);

  useEffect(() => {
    if (mode === 'plan') {
      setIsSiteInspectorExpanded(true);
      return;
    }
    setIsSiteInspectorExpanded(false);
  }, [mode, selectedEntity]);

  const timelineEvents = useMemo(() => {
    if (mode === 'plan') {
      return capacityPrediction.recommendations.slice(0, 6).map((item, index) => ({
        id: `plan-${item.id}-${index}`,
        title: item.title,
        detail: item.summary,
        timestamp: item.metrics?.daysToFull != null ? `ETA ${item.metrics.daysToFull} hari` : 'Perlu validasi lapangan',
        tone: item.urgency?.tone === 'critical' ? 'danger' : item.urgency?.tone === 'high' ? 'warn' : 'info',
      }));
    }
    return incidentQueue.slice(0, 8).map((item) => ({
      id: item.id,
      title: item.title,
      detail: item.subtitle,
      timestamp: new Date(item.timestamp).toLocaleString('id-ID'),
      tone: item.severity === 'critical' ? 'danger' : item.severity === 'high' ? 'warn' : 'info',
    }));
  }, [mode, capacityPrediction.recommendations, incidentQueue]);

  const mapCenter = useMemo(() => {
    if (selectedEntity?.coordinates) return selectedEntity.coordinates;
    if (Array.isArray(selectedSite?.coordinates)) return selectedSite.coordinates;
    const firstSitePoint = siteGroups.find((site) => Array.isArray(site.coordinates));
    if (firstSitePoint?.coordinates) return firstSitePoint.coordinates;
    const firstPoint = layerFilteredEntities.find((item) => Array.isArray(item.coordinates));
    return firstPoint?.coordinates || DEFAULT_CENTER;
  }, [layerFilteredEntities, selectedEntity, selectedSite, siteGroups]);

  const mapLinks = useMemo(() => {
    if (!layers.links) return [];
    const links = [];
    filteredOdps.forEach((odp) => {
      const olt = allOltsById[String(odp.olt_id || '')];
      if (!olt) return;
      if (!hasValidCoordinates(olt.latitude, olt.longitude) || !hasValidCoordinates(odp.latitude, odp.longitude)) return;
      links.push({ id: `olt-odp-${olt.id}-${odp.id}`, path: [[Number(olt.latitude), Number(olt.longitude)], [Number(odp.latitude), Number(odp.longitude)]], type: normalizeKriteria(olt.kriteria) === 'plan' || normalizeKriteria(odp.kriteria) === 'plan' ? 'plan' : 'live' });
    });
    mapCustomers.forEach((customer) => {
      const odpId = String(customer.linked_odp_id || customer.odp_id || '');
      const odp = filteredOdpsById[odpId];
      if (!odp) return;
      if (!hasValidCoordinates(customer.latitude, customer.longitude) || !hasValidCoordinates(odp.latitude, odp.longitude)) return;
      links.push({ id: `odp-customer-${odp.id}-${customer.id}`, path: [[Number(odp.latitude), Number(odp.longitude)], [Number(customer.latitude), Number(customer.longitude)]], type: 'customer' });
    });
    return links;
  }, [layers.links, filteredOdps, allOltsById, mapCustomers, filteredOdpsById]);

  const selectedIncidents = useMemo(() => {
    if (!selectedEntity) return [];
    if (selectedEntity.type === 'incident') return [selectedEntity.data];
    return incidentQueue.filter((item) => item.linkedEntityKeys.includes(selectedEntity.key));
  }, [selectedEntity, incidentQueue]);

  const selectedPrimaryIncident = useMemo(() => selectedIncidents[0] || null, [selectedIncidents]);

  const selectedCustomerId = useMemo(() => {
    if (!selectedEntity) return '';
    if (selectedEntity.type === 'customer') return String(selectedEntity.id || '');
    if (selectedEntity.type === 'incident') {
      const linkedCustomerRef = toArray(selectedEntity.data?.linkedEntityKeys).find((item) => String(item || '').startsWith('customer:'));
      return linkedCustomerRef ? String(linkedCustomerRef).split(':')[1] : '';
    }
    const linkedIncident = selectedPrimaryIncident;
    if (!linkedIncident) return '';
    const linkedCustomerRef = toArray(linkedIncident.linkedEntityKeys).find((item) => String(item || '').startsWith('customer:'));
    return linkedCustomerRef ? String(linkedCustomerRef).split(':')[1] : '';
  }, [selectedEntity, selectedPrimaryIncident]);

  const selectedInfraRef = useMemo(() => {
    if (!selectedEntity) return null;
    if (['nas', 'olt', 'odp'].includes(selectedEntity.type)) {
      return { type: selectedEntity.type, id: String(selectedEntity.id || '') };
    }
    if (selectedEntity.type === 'customer') {
      const odpId = String(selectedEntity.data?.linked_odp_id || selectedEntity.data?.odp_id || '').trim();
      if (odpId) return { type: 'odp', id: odpId };
      const oltId = String(selectedEntity.data?.linked_olt_id || selectedEntity.data?.olt_id || '').trim();
      if (oltId) return { type: 'olt', id: oltId };
      const nasId = String(selectedEntity.data?.router_id || '').trim();
      if (nasId) return { type: 'nas', id: nasId };
    }
    const fromIncident = toArray(selectedPrimaryIncident?.linkedEntityKeys)
      .map((item) => String(item || ''))
      .find((item) => item.startsWith('nas:') || item.startsWith('olt:') || item.startsWith('odp:'));
    if (!fromIncident) return null;
    const [type, id] = fromIncident.split(':');
    return { type, id };
  }, [selectedEntity, selectedPrimaryIncident]);

  const loadRouterCatalog = useCallback(async () => {
    const headers = getHeaders();
    if (!headers) {
      setRouterCatalogReady(true);
      return;
    }
    try {
      const response = await fetch(`${API}/routers`, { headers });
      if (!response.ok) throw new Error('Gagal memuat katalog router.');
      const payload = await response.json();
      setRouterCatalog(Array.isArray(payload) ? payload : []);
    } catch (_error) {
      setRouterCatalog([]);
    } finally {
      setRouterCatalogReady(true);
    }
  }, [getHeaders]);

  useEffect(() => {
    loadRouterCatalog();
  }, [loadRouterCatalog]);

  const routerOptionById = useMemo(() => {
    const mapped = new Map();
    const catalogRows = Array.isArray(routerCatalog) && routerCatalog.length > 0 ? routerCatalog : nasRouters;
    catalogRows.forEach((item) => {
      const id = String(item?.id || item?.router_id || '').trim();
      if (!id) return;
      mapped.set(id, {
        id,
        name: String(item?.name || item?.identity || item?.nas_identifier || id),
        role: normalizeRouterRoleToken(item?.router_role || item?.role),
        site: String(item?.site_name || item?.site || item?.site_id || '-'),
        nasIdentifier: String(item?.nas_identifier || '').trim(),
      });
    });
    return mapped;
  }, [routerCatalog, nasRouters]);

  const selectedRouterContext = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'nas') {
      return {
        isValid: false,
        resolvedRouterId: '',
        reason: 'Relasi router hanya tersedia saat node Router/NAS dipilih.',
      };
    }

    const source = selectedEntity.data || {};
    const candidates = new Set(
      [
        selectedEntity.id,
        source.id,
        source.router_id,
        source.nas_identifier,
      ].map((value) => String(value || '').trim()).filter(Boolean),
    );

    let resolvedRouterId = '';
    for (const [routerId, routerMeta] of routerOptionById.entries()) {
      if (candidates.has(routerId) || (routerMeta.nasIdentifier && candidates.has(routerMeta.nasIdentifier))) {
        resolvedRouterId = routerId;
        break;
      }
    }

    if (!resolvedRouterId) {
      return {
        isValid: false,
        resolvedRouterId: '',
        reason: 'Relasi router belum tersedia untuk node ini.',
      };
    }

    return {
      isValid: true,
      resolvedRouterId,
      reason: '',
    };
  }, [selectedEntity, routerOptionById]);

  const loadRouterRelations = useCallback(async (routerId) => {
    const targetRouterId = String(routerId || '').trim();
    if (!targetRouterId) {
      setRouterRelations([]);
      setRouterRelationsError('');
      return;
    }
    const headers = getHeaders();
    if (!headers) return;
    setRouterRelationsLoading(true);
    setRouterRelationsError('');
    try {
      const response = await fetch(`${API}/routers/${encodeURIComponent(targetRouterId)}/relations`, { headers });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(String(errorBody?.detail || 'Gagal memuat relasi router.'));
      }
      const payload = await response.json();
      setRouterRelations(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setRouterRelations([]);
      const detail = String(error?.message || '').trim();
      if (/not found|tidak ditemukan/i.test(detail)) {
        setRouterRelationsError('Router tidak ditemukan di katalog aktif.');
      } else {
        setRouterRelationsError(detail || 'Gagal memuat relasi router.');
      }
    } finally {
      setRouterRelationsLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    if (!selectedRouterContext.isValid) {
      setRouterRelations([]);
      setRouterRelationsError('');
      return;
    }
    loadRouterRelations(selectedRouterContext.resolvedRouterId);
  }, [selectedRouterContext, loadRouterRelations]);

  const activeRouterRelations = useMemo(() => (
    routerRelations
      .filter((item) => Boolean(item?.is_active))
      .sort((a, b) => Number(a?.priority_order ?? 999) - Number(b?.priority_order ?? 999))
  ), [routerRelations]);

  const routerRelationsViewState = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'nas') return 'hidden';
    if (!selectedRouterContext.isValid) return 'unavailable';
    if (routerRelationsLoading) return 'loading';
    if (routerRelationsError) return 'error';
    if (!activeRouterRelations.length) return 'empty';
    return 'success';
  }, [selectedEntity, selectedRouterContext.isValid, routerRelationsLoading, routerRelationsError, activeRouterRelations.length]);

  const selectedRouterTelemetryHint = useMemo(() => {
    if (routerRelationsViewState !== 'success') return '';
    const primaryRelation = activeRouterRelations.find((item) => String(item?.relation_type || '').toLowerCase() === 'primary') || activeRouterRelations[0];
    if (!primaryRelation) return '';
    const relationType = String(primaryRelation?.relation_type || 'primary').toLowerCase();
    const linkStatus = String(primaryRelation?.link_status || 'unknown').toLowerCase();
    return `${RELATION_TYPE_LABELS[relationType] || relationType} | ${linkStatus}`;
  }, [routerRelationsViewState, activeRouterRelations]);

  const selectedTicketContext = useMemo(() => {
    if (!selectedEntity) return null;
    const sourceType = selectedEntity.type === 'nas'
      ? 'router'
      : selectedEntity.type === 'customer'
        ? 'customer'
        : selectedEntity.type === 'incident'
          ? 'incident'
          : selectedEntity.type === 'site'
            ? 'site'
            : 'manual';
    const incidentId = selectedPrimaryIncident?.id || '';
    const customerId = selectedEntity.type === 'customer' ? String(selectedEntity.id || '') : (selectedCustomerId || '');
    const routerId = selectedEntity.type === 'nas'
      ? String(selectedEntity.id || '')
      : (selectedInfraRef?.type === 'nas' ? String(selectedInfraRef.id || '') : '');
    return {
      sourceType,
      sourceId: String(selectedEntity.id || ''),
      incidentId,
      customerId,
      routerId,
      siteId: String(selectedEntity.siteId || selectedSiteKey || ''),
    };
  }, [selectedEntity, selectedPrimaryIncident, selectedCustomerId, selectedInfraRef, selectedSiteKey]);

  const executionLogContext = useMemo(() => {
    if (!selectedEntity || !selectedTicketContext) return null;
    return {
      sourceType: selectedTicketContext.sourceType,
      sourceId: selectedTicketContext.sourceId,
      incidentId: selectedTicketContext.incidentId || '',
      customerId: selectedTicketContext.customerId || '',
      routerId: selectedTicketContext.routerId || '',
      nodeId: selectedTicketContext.routerId || '',
      siteId: selectedTicketContext.siteId || '',
      ticketId: String(relatedTickets[0]?.id || ''),
    };
  }, [selectedEntity, selectedTicketContext, relatedTickets]);

  useEffect(() => {
    const loadRelatedTickets = async () => {
      if (!selectedEntity || !selectedTicketContext) {
        setRelatedTickets([]);
        setTicketsError('');
        setTicketsLoading(false);
        return;
      }
      const headers = getHeaders();
      if (!headers) return;
      setTicketsLoading(true);
      setTicketsError('');
      try {
        const payload = await fetchTickets({
          headers,
          params: {
            source_type: selectedTicketContext.sourceType,
            source_id: selectedTicketContext.sourceId,
            customer_id: selectedTicketContext.customerId || undefined,
            incident_id: selectedTicketContext.incidentId || undefined,
            router_id: selectedTicketContext.routerId || undefined,
            site_id: selectedTicketContext.siteId || undefined,
            limit: 20,
          },
        });
        setRelatedTickets(payload.items || []);
      } catch (err) {
        setRelatedTickets([]);
        setTicketsError(String(err?.message || 'Gagal memuat ticket terkait.'));
      } finally {
        setTicketsLoading(false);
      }
    };
    loadRelatedTickets();
  }, [selectedEntity, selectedTicketContext, getHeaders]);

  useEffect(() => {
    let cancelled = false;
    const loadExecutionLogs = async () => {
      if (!executionLogContext) {
        setExecutionLogs([]);
        setExecutionLogsError('');
        setExecutionLogsLoading(false);
        setShowAllExecutionLogs(false);
        return;
      }
      setExecutionLogsLoading(true);
      setExecutionLogsError('');
      try {
        const payload = await fetchExecutionLogs({
          apiBase: API,
          navigate,
          params: {
            source_type: executionLogContext.sourceType,
            source_id: executionLogContext.sourceId,
            incident_id: executionLogContext.incidentId || undefined,
            ticket_id: executionLogContext.ticketId || undefined,
            customer_id: executionLogContext.customerId || undefined,
            router_id: executionLogContext.routerId || undefined,
            site_id: executionLogContext.siteId || undefined,
            limit: 120,
          },
        });
        if (cancelled) return;
        setExecutionLogs(payload.items || []);
      } catch (error) {
        if (cancelled) return;
        setExecutionLogs([]);
        setExecutionLogsError(String(error?.message || 'Gagal memuat execution log.'));
      } finally {
        if (!cancelled) setExecutionLogsLoading(false);
      }
    };
    loadExecutionLogs();
    return () => {
      cancelled = true;
    };
  }, [executionLogContext, navigate]);

  const activeRelatedTickets = useMemo(
    () => relatedTickets.filter((ticket) => String(ticket.status || '').toLowerCase() !== 'closed'),
    [relatedTickets],
  );

  const latestRelatedTicket = useMemo(
    () => relatedTickets[0] || null,
    [relatedTickets],
  );

  const handleCreateTicket = useCallback(async () => {
    if (!selectedEntity || !selectedTicketContext) return;
    const headers = getHeaders();
    if (!headers) return;
    const title = String(ticketDraft.title || '').trim();
    if (!title) {
      toast.error('Judul ticket wajib diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      const result = await createTicket({
        headers,
        payload: {
          title,
          description: ticketDraft.description,
          priority: ticketDraft.priority,
          assigned_to: ticketDraft.assigned_to || undefined,
          source_type: selectedTicketContext.sourceType,
          source_id: selectedTicketContext.sourceId,
          customer_id: selectedTicketContext.customerId || undefined,
          router_id: selectedTicketContext.routerId || undefined,
          node_id: selectedTicketContext.routerId || undefined,
          site_id: selectedTicketContext.siteId || undefined,
          incident_id: selectedTicketContext.incidentId || undefined,
        },
      });
      result.warnings.forEach((warning) => toast.warning(warning));
      toast.success(`Ticket ${result.item.ticketCode || result.item.id} dibuat.`);
      setRelatedTickets((prev) => [result.item, ...prev]);
      setTicketDraft({ title: '', description: '', priority: 'medium', assigned_to: '' });
    } catch (error) {
      toast.error(error?.message || 'Gagal membuat ticket.');
    } finally {
      setTicketSaving(false);
    }
  }, [selectedEntity, selectedTicketContext, getHeaders, ticketDraft]);

  const handleAddTicketUpdate = useCallback(async (ticketId) => {
    const headers = getHeaders();
    if (!headers || !ticketId) return;
    const note = String(ticketUpdateNote || '').trim();
    if (!note) {
      toast.error('Catatan update belum diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      const updated = await appendTicketUpdate({ headers, ticketId, note });
      setRelatedTickets((prev) => prev.map((item) => (String(item.id) === String(ticketId) ? updated : item)));
      setTicketUpdateNote('');
      toast.success('Worklog ticket tersimpan.');
    } catch (error) {
      toast.error(error?.message || 'Gagal menambah worklog ticket.');
    } finally {
      setTicketSaving(false);
    }
  }, [getHeaders, ticketUpdateNote]);

  const visibleExecutionLogs = useMemo(() => {
    const logs = Array.isArray(executionLogs) ? executionLogs : [];
    return showAllExecutionLogs ? logs : logs.slice(0, 5);
  }, [executionLogs, showAllExecutionLogs]);
  const latestExecutionLog = useMemo(
    () => (Array.isArray(executionLogs) && executionLogs.length > 0 ? executionLogs[0] : null),
    [executionLogs],
  );

  const createExecutionEntry = useCallback(async ({ actionType, message, context = executionLogContext }) => {
    if (!context || !message) return null;
    try {
      const created = await createExecutionLog({
        apiBase: API,
        navigate,
        payload: {
          source_type: context.sourceType,
          source_id: context.sourceId,
          incident_id: context.incidentId || undefined,
          ticket_id: context.ticketId || undefined,
          customer_id: context.customerId || undefined,
          router_id: context.routerId || undefined,
          node_id: context.nodeId || undefined,
          site_id: context.siteId || undefined,
          action_type: actionType,
          message,
        },
      });
      if (created) {
        setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      }
      return created;
    } catch (_error) {
      return null;
    }
  }, [executionLogContext, navigate]);

  const handleAddExecutionNote = useCallback(async () => {
    const note = String(executionNote || '').trim();
    if (!note) {
      toast.error('Catatan execution log belum diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      const created = await createExecutionEntry({ actionType: 'manual_note', message: note });
      if (!created) {
        throw new Error('Execution log gagal disimpan.');
      }
      setExecutionNote('');
      toast.success('Execution log tersimpan.');
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menyimpan execution log.'));
    } finally {
      setTicketSaving(false);
    }
  }, [executionNote, createExecutionEntry]);

  const handleRunbookPreset = useCallback(async (preset) => {
    if (!preset) return;
    const contextLabel = selectedEntity?.name || selectedEntity?.id || '';
    const message = buildRunbookMessage({ presetId: preset.id, contextLabel });
    if (shouldSkipRunbookDuplicate({ logs: executionLogs, actionType: preset.actionType || 'manual_note', message })) {
      toast.info('Preset sama baru saja dicatat. Menghindari log duplikat.');
      return;
    }
    setTicketSaving(true);
    try {
      const created = await createExecutionEntry({
        actionType: preset.actionType || 'manual_note',
        message,
      });
      if (!created) throw new Error('Execution log gagal disimpan.');
      if (relatedTickets[0]?.id && shouldMirrorRunbookToTicket({ presetId: preset.id, actionType: preset.actionType })) {
        const headers = getHeaders();
        if (headers) {
          const updated = await appendTicketUpdate({
            headers,
            ticketId: relatedTickets[0].id,
            note: buildTicketShortcutNote(preset.label, contextLabel),
          });
          setRelatedTickets((prev) => prev.map((item) => (String(item.id) === String(updated.id) ? updated : item)));
        }
      }
      toast.success(`Preset "${preset.label}" tercatat.`);
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menjalankan preset runbook.'));
    } finally {
      setTicketSaving(false);
    }
  }, [createExecutionEntry, getHeaders, relatedTickets, selectedEntity, executionLogs]);

  const planSiteContext = useMemo(() => {
    if (selectedSiteKey) return String(selectedSiteKey);
    if (selectedEntity?.siteId) return String(selectedEntity.siteId);
    if (siteFilter !== 'all') return String(siteFilter);
    return '';
  }, [selectedSiteKey, selectedEntity, siteFilter]);

  const infrastructureEditLink = useMemo(() => {
    if (!selectedEntity) return '/infrastructure';
    if (selectedEntity.type === 'olt') {
      return `/infrastructure?action=edit-olt&nodeType=olt&nodeId=${encodeURIComponent(String(selectedEntity.id || ''))}`;
    }
    if (selectedEntity.type === 'odp') {
      return `/infrastructure?action=edit-odp&nodeType=odp&nodeId=${encodeURIComponent(String(selectedEntity.id || ''))}`;
    }
    if (selectedInfraRef) {
      return `/infrastructure?nodeType=${encodeURIComponent(selectedInfraRef.type)}&nodeId=${encodeURIComponent(selectedInfraRef.id)}`;
    }
    return '/infrastructure';
  }, [selectedEntity, selectedInfraRef]);

  const infrastructureCreateOltLink = useMemo(() => {
    const query = new URLSearchParams();
    query.set('action', 'create-olt');
    if (planSiteContext) query.set('siteId', planSiteContext);
    if (selectedEntity?.coordinates?.length === 2) {
      query.set('latitude', String(selectedEntity.coordinates[0]));
      query.set('longitude', String(selectedEntity.coordinates[1]));
    }
    if (selectedEntity?.type) query.set('sourceType', String(selectedEntity.type));
    if (selectedEntity?.id) query.set('sourceId', String(selectedEntity.id));
    return `/infrastructure?${query.toString()}`;
  }, [planSiteContext, selectedEntity]);

  const infrastructureCreateOdpLink = useMemo(() => {
    const query = new URLSearchParams();
    query.set('action', 'create-odp');
    if (selectedEntity?.type === 'olt') query.set('oltId', String(selectedEntity.id || ''));
    if (planSiteContext) query.set('siteId', planSiteContext);
    if (selectedEntity?.coordinates?.length === 2) {
      query.set('latitude', String(selectedEntity.coordinates[0]));
      query.set('longitude', String(selectedEntity.coordinates[1]));
    }
    if (selectedEntity?.type) query.set('sourceType', String(selectedEntity.type));
    if (selectedEntity?.id) query.set('sourceId', String(selectedEntity.id));
    return `/infrastructure?${query.toString()}`;
  }, [selectedEntity, planSiteContext]);

  const selectedRecommendations = useMemo(() => {
    if (mode !== 'plan') return [];
    if (selectedEntity?.type === 'odp') return capacityPrediction.recommendations.filter((item) => String(item.sourceId) === String(selectedEntity.id)).slice(0, 3);
    return capacityPrediction.recommendations.slice(0, 3);
  }, [mode, selectedEntity, capacityPrediction.recommendations]);

  const planDecisionSupport = useMemo(() => {
    if (mode !== 'plan' || !selectedEntity) return null;

    let utilization = '-';
    let threshold = '85% warning / 95% critical';
    let readiness = 'Perlu Verifikasi';
    let nearestHint = 'Nearest infra belum tersedia pada scope ini.';
    let nextStep = 'Validasi data kapasitas, lalu tentukan aksi planning.';
    let planningNote = 'Gunakan utilisasi aktual + demand area sebelum ekspansi.';
    let demandNote = 'Demand trend belum cukup untuk prediksi agresif.';
    let riskIfNoAction = 'Risiko belum terukur.';

    if (selectedEntity.type === 'odp') {
      const prediction = capacityPrediction.byOdpId[String(selectedEntity.id)] || null;
      if (prediction) {
        utilization = `${prediction.used}/${prediction.capacity}`;
        const risk = String(prediction.risk?.level || 'safe');
        readiness = risk === 'critical' ? 'Kritis' : risk === 'high' ? 'Waspada Tinggi' : risk === 'warning' ? 'Monitor Ketat' : 'Siap';
        nextStep = risk === 'critical' || risk === 'high'
          ? 'Siapkan draft ekspansi ODP dan tetapkan survey lapangan.'
          : 'Lanjutkan monitoring kapasitas mingguan.';
        planningNote = prediction.insight || planningNote;
        demandNote = `Growth 30d +${prediction.growth30d || 0} pelanggan.`;
        riskIfNoAction = risk === 'critical'
          ? 'Potensi overload ODP dan delay aktivasi pelanggan baru.'
          : risk === 'high'
            ? 'Risiko saturasi dalam horizon pendek.'
            : 'Risiko rendah, tetap pantau pertumbuhan area.';
      }
      if (Array.isArray(selectedEntity.coordinates)) {
        const nearest = filteredOdps
          .filter((item) => String(item.id) !== String(selectedEntity.id) && hasValidCoordinates(item.latitude, item.longitude))
          .map((item) => ({
            name: item.name,
            distance: distanceMeters(selectedEntity.coordinates, [Number(item.latitude), Number(item.longitude)]),
          }))
          .sort((a, b) => a.distance - b.distance)[0];
        if (nearest && Number.isFinite(nearest.distance)) {
          nearestHint = `${nearest.name} (~${Math.round(nearest.distance)}m)`;
        }
      }
    } else if (selectedEntity.type === 'olt') {
      const linkedOdps = filteredOdps.filter((item) => String(item.olt_id || '') === String(selectedEntity.id));
      const used = linkedOdps.reduce((acc, item) => {
        const pred = capacityPrediction.byOdpId[String(item.id)];
        return acc + Number(pred?.used || 0);
      }, 0);
      const cap = linkedOdps.reduce((acc, item) => {
        const pred = capacityPrediction.byOdpId[String(item.id)];
        return acc + Number(pred?.capacity || 0);
      }, 0);
      utilization = cap > 0 ? `${used}/${cap}` : '-';
      const usagePct = cap > 0 ? Math.round((used / cap) * 100) : 0;
      readiness = usagePct >= 85 ? 'Padat' : usagePct >= 70 ? 'Menengah' : 'Longgar';
      nextStep = usagePct >= 85 ? 'Rencanakan split ODP atau tambah uplink OLT.' : 'Pertahankan kapasitas sambil monitor demand.';
      planningNote = `OLT melayani ${linkedOdps.length} ODP pada scope aktif.`;
      demandNote = `Total demand terhubung ${used}/${cap || 0}.`;
      riskIfNoAction = usagePct >= 85 ? 'Risiko bottleneck uplink dan degradasi provisioning.' : 'Risiko rendah untuk jangka pendek.';
    } else if (selectedEntity.type === 'customer') {
      const odpId = String(selectedEntity.data?.linked_odp_id || selectedEntity.data?.odp_id || '');
      const pred = capacityPrediction.byOdpId[odpId];
      if (pred) {
        utilization = `${pred.used}/${pred.capacity}`;
        threshold = `${Math.round((pred.used / Math.max(1, pred.capacity)) * 100)}% current vs target <= 80%`;
        readiness = pred.risk?.label || readiness;
        nextStep = 'Evaluasi kapasitas ODP linked sebelum aktivasi layanan baru.';
        planningNote = `Pelanggan terhubung ke ODP ${odpId}.`;
        demandNote = `ODP linked growth 30d +${pred.growth30d || 0}.`;
        riskIfNoAction = ['critical', 'high'].includes(String(pred.risk?.level || '').toLowerCase())
          ? 'Potensi gagal provision order baru.'
          : 'Dampak rendah, tetap monitor sebelum scale up.';
      }
    }

    return { utilization, threshold, readiness, nearestHint, nextStep, planningNote, demandNote, riskIfNoAction };
  }, [mode, selectedEntity, capacityPrediction.byOdpId, filteredOdps]);

  const selectedRecentEvent = useMemo(() => timelineEvents[0] || null, [timelineEvents]);
  const selectedProbableCause = useMemo(() => {
    if (selectedEntity?.type === 'incident') return selectedEntity?.data?.subtitle || 'Pola gangguan terdeteksi dari anomali traffic.';
    if (selectedIncidents[0]?.subtitle) return selectedIncidents[0].subtitle;
    if (selectedEntity?.type === 'odp') return 'Kapasitas ODP mendekati batas utilisasi.';
    if (selectedEntity?.type === 'customer') return 'Gangguan akses pelanggan terkait endpoint/last-mile.';
    return 'Analisa sedang berlangsung berdasarkan korelasi status entitas.';
  }, [selectedEntity, selectedIncidents]);

  const customerImpactSummary = useMemo(() => deriveCustomerImpactSummary({
    context: selectedEntity
      ? {
        type: selectedEntity.type === 'nas' ? 'router' : selectedEntity.type,
        id: String(selectedEntity.id || ''),
        incident: selectedEntity.type === 'incident' ? (selectedEntity.data || null) : selectedPrimaryIncident,
        incidentId: selectedPrimaryIncident?.id || '',
        customerId: selectedCustomerId || '',
        routerId: selectedEntity.type === 'nas' ? String(selectedEntity.id || '') : (selectedInfraRef?.type === 'nas' ? String(selectedInfraRef.id || '') : ''),
        siteId: String(selectedEntity.siteId || selectedSiteKey || ''),
      }
      : {
        type: '',
        id: '',
        incident: null,
      },
    data: {
      customers: customersForView,
      odps: filteredOdps,
      olts: filteredOlts,
      sites,
      incidents: incidentQueue,
    },
    relations: selectedEntity?.type === 'nas' ? activeRouterRelations : [],
  }), [selectedEntity, selectedPrimaryIncident, selectedCustomerId, selectedInfraRef, selectedSiteKey, customersForView, filteredOdps, filteredOlts, sites, incidentQueue, activeRouterRelations]);

  const siteImpactSummary = useMemo(() => deriveSiteImpactSummary({
    context: selectedEntity
      ? {
        type: selectedEntity.type === 'nas' ? 'router' : selectedEntity.type,
        id: String(selectedEntity.id || ''),
        incident: selectedEntity.type === 'incident' ? (selectedEntity.data || null) : selectedPrimaryIncident,
        incidentId: selectedPrimaryIncident?.id || '',
        customerId: selectedCustomerId || '',
        routerId: selectedEntity.type === 'nas' ? String(selectedEntity.id || '') : (selectedInfraRef?.type === 'nas' ? String(selectedInfraRef.id || '') : ''),
        siteId: String(selectedEntity.siteId || selectedSiteKey || ''),
      }
      : {
        type: '',
        id: '',
        incident: null,
      },
    data: {
      customers: customersForView,
      incidents: incidentQueue,
      tickets: relatedTickets,
      odps: filteredOdps,
      olts: filteredOlts,
      nasRouters,
      sites,
    },
    relations: selectedEntity?.type === 'nas' ? activeRouterRelations : [],
    executionLogs,
    limit: 5,
  }), [
    selectedEntity,
    selectedPrimaryIncident,
    selectedCustomerId,
    selectedInfraRef,
    selectedSiteKey,
    customersForView,
    incidentQueue,
    relatedTickets,
    filteredOdps,
    filteredOlts,
    nasRouters,
    sites,
    activeRouterRelations,
    executionLogs,
  ]);

  const keyStats = useMemo(() => {
    const degradedCustomers = mapCustomers.filter((item) => resolveStatusTone(item.status) !== 'ok').length;
    const highRiskOdps = capacityPrediction.odps.filter((item) => ['critical', 'high'].includes(String(item.risk?.level || 'safe'))).length;
    const planReadinessIssues = capacityPrediction.odps.filter((item) => ['critical', 'high', 'warning'].includes(String(item.risk?.level || 'safe'))).length;
    return {
      degradedCustomers,
      highRiskOdps,
      planReadinessIssues,
      incidents: incidentQueue.length,
      recommendations: capacityPrediction.recommendations.length,
    };
  }, [mapCustomers, capacityPrediction.odps, incidentQueue, capacityPrediction.recommendations]);

  const workspaceGridClass = showFilterRail
    ? (isDetailPanelOpen ? 'grid gap-4 xl:grid-cols-[236px_minmax(0,1fr)_296px]' : 'grid gap-4 xl:grid-cols-[236px_minmax(0,1fr)]')
    : (isDetailPanelOpen ? 'grid gap-4 xl:grid-cols-[minmax(0,1fr)_296px]' : 'grid gap-4 xl:grid-cols-[minmax(0,1fr)]');
  const mapHeightClass = mode === 'live' ? 'h-[680px]' : 'h-[640px]';
  const timelineMaxHeightClass = timelineExpanded ? 'max-h-[320px]' : 'max-h-[180px]';
  const hasSiteMarkers = siteGroups.some((site) => Array.isArray(site.coordinates));
  const interactionState = useMemo(() => {
    if (selectedEntity && isDetailPanelOpen) return 'node-investigation';
    if (selectedEntity) return 'node-quick-inspect';
    return 'site-browsing';
  }, [selectedEntity, isDetailPanelOpen]);
  const isSiteBrowsingState = interactionState === 'site-browsing';
  const isNodeQuickInspectState = interactionState === 'node-quick-inspect';
  const isNodeInvestigationState = interactionState === 'node-investigation';

  const selectNodeFromSite = useCallback((entityKey) => {
    setSelectedEntityKey(entityKey);
  }, []);

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Topology Workspace"
          title="Topology Workspace"
          subtitle="Satu workspace untuk investigasi live dan perencanaan FTTH tanpa nested-tab."
          action={(
            <div className="flex items-center gap-2">
              <UIButton variant={mode === 'live' ? 'primary' : 'ghost'} onClick={() => updateMode('live')}>Live Monitor</UIButton>
              <UIButton variant={mode === 'plan' ? 'primary' : 'ghost'} onClick={() => updateMode('plan')}>Plan / Design</UIButton>
              <UIButton variant="subtle" onClick={fetchTopology} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</UIButton>
            </div>
          )}
        />

        <div className={workspaceGridClass}>
          {showFilterRail && (
          <SectionContainer className="space-y-4">
            <div>
              <p className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Filter Rail</p>
              <p className="cc-text-secondary mt-1 text-xs">{mode === 'live' ? 'Fokus investigasi gangguan aktif, dampak pelanggan, dan jalur recovery.' : 'Fokus kapasitas ODP/OLT, readiness, dan saran ekspansi.'}</p>
            </div>
            <div className="space-y-2">
              <label className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Kategori Infrastruktur</label>
              <select value={infraScope} onChange={(event) => setInfraScope(event.target.value)} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm"><option value="all">Semua</option><option value="live">Live</option><option value="plan">Plan</option></select>
            </div>
            <div className="space-y-2">
              <label className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Site</label>
              <select value={siteFilter} onChange={(event) => setSiteFilter(event.target.value)} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm"><option value="all">Semua Site</option>{siteOptions.map((site) => <option key={site} value={site}>{site}</option>)}</select>
            </div>
            <div className="space-y-2">
              <label className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Layer</label>
              {[{ key: 'customer', label: 'Customer' }, { key: 'odp', label: 'ODP' }, { key: 'olt', label: 'OLT' }, { key: 'incident', label: 'Incident' }, { key: 'links', label: 'Links' }].map((layer) => (
                <button key={layer.key} type="button" onClick={() => setLayers((prev) => ({ ...prev, [layer.key]: !prev[layer.key] }))} className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm ${layers[layer.key] ? 'border-cyan-400/40 bg-cyan-500/10 text-cyan-100' : 'border-white/10 bg-[#0a1322] text-slate-300'}`}>
                  <span>{layer.label}</span><span className="text-xs">{layers[layer.key] ? 'ON' : 'OFF'}</span>
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><p className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Entity Scope</p><StatusBadge tone="info">{layerFilteredEntities.length}</StatusBadge></div>
              {mode === 'plan' ? (
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Cari entity scope..."
                  className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500"
                />
              ) : null}
              <div className="max-h-[340px] space-y-1.5 overflow-auto pr-1">
                {layerFilteredEntities.length === 0 ? <p className="cc-text-secondary rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-xs">Tidak ada entitas pada scope ini. Cek filter/layer atau relasi belum lengkap.</p> : layerFilteredEntities.map((entity) => (
                  <button key={entity.key} type="button" onClick={() => setSelectedEntityKey(entity.key)} className={`w-full rounded-lg border px-2.5 py-2 text-left ${selectedEntityKey === entity.key ? 'border-cyan-400/50 bg-cyan-500/10' : 'border-white/10 bg-[#0a1322] hover:bg-[#101b2e]'}`}>
                    <div className="mb-1 flex items-center justify-between gap-2"><p className="text-sm font-semibold text-white">{entity.name}</p><StatusBadge tone={resolveStatusTone(entity.status)}>{String(entity.status || 'unknown').toUpperCase()}</StatusBadge></div>
                    <p className="line-clamp-2 text-[11px] text-slate-400">{entity.subtitle}</p>
                  </button>
                ))}
              </div>
            </div>
          </SectionContainer>
          )}

          <div className="space-y-4">
            <SectionContainer className="space-y-3">
              <div className="flex items-center justify-between">
                <div><p className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Map / Topology Canvas</p><p className="cc-text-secondary text-xs">{mode === 'live' ? 'Investigasi gangguan dan relasi dampak pelanggan secara langsung.' : 'Validasi rencana kapasitas, cluster demand, dan prioritas ekspansi.'}</p></div>
                <div className="flex items-center gap-2 text-xs">
                  <StatusBadge tone={mode === 'live' ? 'info' : 'neutral'}>{mode === 'live' ? 'LIVE' : 'PLAN'}</StatusBadge>
                  <button type="button" onClick={() => setShowTimeline((prev) => !prev)} className="rounded-md border border-white/15 px-2 py-1 text-slate-300">{showTimeline ? 'Timeline Off' : 'Timeline On'}</button>
                  <button type="button" onClick={() => setShowFilterRail((prev) => !prev)} className="rounded-md border border-white/15 px-2 py-1 text-slate-300">
                    {showFilterRail ? 'Filter Off' : 'Filter On'}
                  </button>
                  <button
                    type="button"
                    disabled={mode === 'live' && !selectedEntity}
                    onClick={() => setIsDetailPanelOpen((prev) => !prev)}
                    className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-slate-300 disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {isDetailPanelOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
                    {isDetailPanelOpen ? 'Tutup Detail' : 'Buka Detail'}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Cari entitas, kode, atau lokasi..."
                  className="min-w-[240px] flex-1 rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500"
                />
                <span className="rounded-md border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs text-slate-300">{siteGroups.length} site</span>
                <span className="rounded-md border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs text-slate-300">{layerFilteredEntities.length} entitas</span>
              </div>

              <div className={`${mapHeightClass} relative overflow-hidden rounded-xl border border-white/10`}>
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  className="h-full w-full"
                  scrollWheelZoom
                  whenReady={(event) => onLeafletMapReady(event?.target)}
                >
                  <LeafletMapReadyBridge onReady={onLeafletMapReady} debugEnabled={isDeepLinkDebugEnabled()} />
                  <LeafletTileLayer />
                  {mapLinks.map((link) => <Polyline key={link.id} positions={link.path} pathOptions={{ color: link.type === 'plan' ? '#f59e0b' : link.type === 'customer' ? '#22c55e' : '#3b82f6', weight: link.type === 'customer' ? 1.5 : 2.2, opacity: link.type === 'customer' ? 0.45 : 0.72, dashArray: link.type === 'plan' ? '6 4' : undefined }} />)}
                  {siteGroups.map((site) => {
                    if (!Array.isArray(site.coordinates)) return null;
                    const selected = selectedSiteKey === site.key;
                    const baseColor = site.tone === 'danger' ? '#ef4444' : site.tone === 'warn' ? '#f59e0b' : site.tone === 'ok' ? '#22c55e' : '#60a5fa';
                    const nodeCount = Number(site.nodes?.length || 0);
                    const radius = selected ? 13 : Math.min(12, Math.max(8, 7 + Math.ceil(nodeCount / 4)));
                    return (
                      <CircleMarker
                        key={`site-${site.key}`}
                        center={site.coordinates}
                        radius={radius}
                        pathOptions={{ color: baseColor, fillColor: baseColor, fillOpacity: selected ? 0.9 : 0.72, weight: 2.2 }}
                        eventHandlers={{
                          click: () => {
                            setSelectedSiteKey(site.key);
                            if (mode === 'live' && !isDetailPanelOpen) {
                              setSelectedEntityKey('');
                            }
                          },
                        }}
                      />
                    );
                  })}
                  {layers.incident && incidentQueue.map((item) => {
                    if (!Array.isArray(item.coordinates)) return null;
                    return <Marker key={item.id} position={item.coordinates} icon={createIncidentIcon(item.severity)} eventHandlers={{ click: () => setSelectedEntityKey(`incident:${item.id}`) }}><Popup><p className="font-semibold">{item.title}</p><p className="text-xs">{item.subtitle}</p></Popup></Marker>;
                  })}
                </MapContainer>

                {!hasSiteMarkers ? (
                  <div className="pointer-events-none absolute bottom-3 right-3 z-[600] rounded-lg border border-white/15 bg-[#081223]/85 px-3 py-2 text-xs text-slate-300 backdrop-blur-sm">
                    Marker site belum tersedia pada scope ini. Cek data `site_id` atau koordinat entity.
                  </div>
                ) : null}

                <div className={`pointer-events-none absolute left-3 top-3 z-[600] ${mode === 'plan' ? 'w-[min(340px,calc(100%-1.5rem))]' : (isSiteInspectorExpanded ? 'w-[min(300px,calc(100%-1.5rem))]' : 'w-[min(240px,calc(100%-1.5rem))]')}`}>
                  <div className={`pointer-events-auto rounded-xl border border-white/15 bg-[#081223]/90 backdrop-blur-sm ${mode === 'plan' ? 'p-2.5' : (isSiteInspectorExpanded ? 'p-3' : 'p-2')} ${mode === 'live' && !isSiteBrowsingState ? 'opacity-85' : 'opacity-100'}`}>
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-300">Site Inspector</p>
                        <p className="mt-1 text-sm font-semibold text-white">{selectedSite?.name || 'Pilih site marker'}</p>
                        <p className="mt-1 text-[11px] text-slate-400">
                          {mode === 'live'
                            ? (isSiteBrowsingState ? 'Baca site cepat, perluas node hanya saat dibutuhkan.' : isNodeQuickInspectState ? 'Quick inspect aktif, inspector diperkecil.' : 'Investigation aktif, fokus ke panel detail kanan.')
                            : 'Analisis site untuk kapasitas, readiness, dan konteks planning.'}
                        </p>
                      </div>
                      <StatusBadge tone={selectedSite?.tone || 'neutral'}>{selectedSite ? `${selectedSite.nodes.length} Node` : 'NO SITE'}</StatusBadge>
                    </div>
                    {selectedSite ? (
                      <div className="mb-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsSiteInspectorExpanded((prev) => !prev)}
                          disabled={mode === 'live' && isNodeInvestigationState}
                          className="rounded-md border border-white/10 px-2 py-1 text-[11px] text-slate-300 disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          {isSiteInspectorExpanded ? 'Ringkas' : 'Perluas'}
                        </button>
                      </div>
                    ) : null}
                    {!selectedSite ? (
                      <p className="text-xs text-slate-400">Klik marker site untuk melihat node dalam site tersebut.</p>
                    ) : (
                      <>
                        <p className="mb-2 text-xs text-slate-400">
                          NAS {selectedSite.counts.nas || 0} ? OLT {selectedSite.counts.olt || 0} ? ODP {selectedSite.counts.odp || 0} ? Customer {selectedSite.counts.customer || 0}
                        </p>
                        {isSiteInspectorExpanded ? (
                          <div className={`space-y-1.5 overflow-auto pr-1 ${mode === 'plan' ? 'max-h-[300px]' : 'max-h-[220px]'}`}>
                            {selectedSite.nodes.slice(0, mode === 'plan' ? 18 : 14).map((node) => (
                              <button
                                key={node.key}
                                type="button"
                                onClick={() => selectNodeFromSite(node.key)}
                                className={`w-full rounded-lg border text-left ${mode === 'plan' ? 'px-2 py-1.5' : 'px-2.5 py-2'} ${selectedEntityKey === node.key ? 'border-cyan-400/50 bg-cyan-500/10' : 'border-white/10 bg-[#0e1b30] hover:bg-[#132744]'}`}
                              >
                                <div className="mb-1 flex items-center justify-between gap-2">
                                  <p className="text-xs font-semibold text-white">{node.name}</p>
                                  <StatusBadge tone={resolveStatusTone(node.status)}>{String(node.type || '').toUpperCase()}</StatusBadge>
                                </div>
                                <p className="text-[11px] text-slate-400">{node.subtitle}</p>
                                {node.type === 'nas' && node.routerRole?.label ? (
                                  <p className="mt-1 text-[10px] text-cyan-300">Role: {node.routerRole.label} ({node.routerRole.source})</p>
                                ) : null}
                              </button>
                            ))}
                            {selectedSite.nodes.length > (mode === 'plan' ? 18 : 14) ? <p className="text-[11px] text-slate-500">+{selectedSite.nodes.length - (mode === 'plan' ? 18 : 14)} node lain pada site ini.</p> : null}
                          </div>
                        ) : (
                          <div className="rounded-lg border border-white/10 bg-[#0e1b30] px-2.5 py-2">
                            {selectedEntity ? (
                              <>
                                <div className="mb-1 flex items-center justify-between gap-2">
                                  <p className="text-xs font-semibold text-white">{selectedEntity.name}</p>
                                  <StatusBadge tone={resolveStatusTone(selectedEntity.status)}>{String(selectedEntity.type || '').toUpperCase()}</StatusBadge>
                                </div>
                                <p className="text-[11px] text-slate-400">{selectedEntity.subtitle}</p>
                                <button
                                  type="button"
                                  onClick={() => setIsSiteInspectorExpanded(true)}
                                  className="mt-2 rounded-md border border-white/10 px-2 py-1 text-[11px] text-slate-300"
                                >
                                  Tampilkan node list
                                </button>
                              </>
                            ) : (
                              <>
                                <p className="text-xs text-slate-300">Ringkasan site aktif.</p>
                                <p className="mt-1 text-[11px] text-slate-400">Node: {selectedSite.nodes.length} ? Incident: {selectedSite.incidentCount}</p>
                                {selectedSite.nodes[0] ? (
                                  <div className="mt-2 rounded-md border border-white/10 bg-[#101b2e] px-2 py-1.5">
                                    <p className="text-[11px] font-semibold text-white">Top node: {selectedSite.nodes[0].name}</p>
                                    <p className="text-[10px] text-slate-400">{selectedSite.nodes[0].subtitle}</p>
                                  </div>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => setIsSiteInspectorExpanded(true)}
                                  className="mt-2 rounded-md border border-white/10 px-2 py-1 text-[11px] text-slate-300"
                                >
                                  Perluas node list
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {mode === 'live' && (
                  <div className="pointer-events-none absolute right-3 top-3 z-[600] w-[320px]">
                    <div className="pointer-events-auto rounded-xl border border-white/15 bg-[#081223]/90 p-3 backdrop-blur-sm">
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-300">Queue Incident</p>
                        <StatusBadge tone="warn">{incidentQueue.length}</StatusBadge>
                      </div>
                      {incidentQueue.length === 0 && !isIncidentQueueExpanded ? (
                        <button
                          type="button"
                          onClick={() => setIsIncidentQueueExpanded(true)}
                          className="w-full rounded-lg border border-white/10 bg-[#0e1b30] px-2.5 py-2 text-left text-xs text-slate-400 hover:bg-[#132744]"
                        >
                          0 incidents. Queue kosong atau relasi incident belum terbentuk.
                        </button>
                      ) : (
                        <>
                          <div className="mb-2 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setIsIncidentQueueExpanded((prev) => !prev)}
                              className="rounded-md border border-white/10 px-2 py-1 text-[11px] text-slate-300"
                            >
                              {isIncidentQueueExpanded ? 'Ringkas' : 'Perluas'}
                            </button>
                          </div>
                          {isIncidentQueueExpanded && (
                            <div className="max-h-[260px] space-y-1.5 overflow-auto pr-1">
                              {incidentQueue.length === 0 ? (
                                <p className="text-xs text-slate-400">Queue kosong pada scope aktif. Monitoring terdeteksi stabil.</p>
                              ) : incidentQueue.slice(0, 8).map((incident) => (
                                <button
                                  key={incident.id}
                                  type="button"
                                  onClick={() => setSelectedEntityKey(`incident:${incident.id}`)}
                                  className="w-full rounded-lg border border-white/10 bg-[#0e1b30] px-2.5 py-2 text-left hover:bg-[#132744]"
                                >
                                  <div className="mb-1 flex items-center justify-between gap-2">
                                    <p className="text-xs font-semibold text-white">{incident.title}</p>
                                    <StatusBadge tone={resolveStatusTone(incident.severity)}>{incident.severity.toUpperCase()}</StatusBadge>
                                  </div>
                                  <p className="text-[11px] text-slate-400">{incident.subtitle}</p>
                                  <p className="mt-1 text-[10px] text-slate-500">Age: {formatAge(incident.timestamp)}</p>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {mode === 'live' && isNodeQuickInspectState && (
                  <div className="pointer-events-none absolute bottom-3 left-3 z-[600] w-[min(360px,calc(100%-1.5rem))]">
                    <div className="pointer-events-auto rounded-xl border border-cyan-300/30 bg-[#081223]/92 p-2.5 backdrop-blur-sm">
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-white">{selectedEntity.name}</p>
                          <p className="text-[11px] text-slate-400">Type: {String(selectedEntity.type || '-').toUpperCase()}</p>
                        </div>
                        <StatusBadge tone={resolveStatusTone(selectedEntity.status)}>{String(selectedEntity.status || 'unknown').toUpperCase()}</StatusBadge>
                      </div>
                      <div className="grid gap-1 text-xs text-slate-300">
                        <p>Impacted customers: <span className="text-white">{customerImpactSummary.counts.total}</span></p>
                        <p>Offline / degraded: <span className="text-white">{customerImpactSummary.counts.offline} / {customerImpactSummary.counts.degraded}</span></p>
                        <p>Site impact: <span className="text-white">{siteImpactSummary.selectedSite ? `${siteImpactSummary.selectedSite.site_name || siteImpactSummary.selectedSite.site_id} (${String(siteImpactSummary.selectedSite.severity || 'low').toUpperCase()})` : 'Belum terdeteksi'}</span></p>
                        {siteImpactSummary.selectedSite ? (
                          <p>Site affected: <span className="text-white">{siteImpactSummary.selectedSite.impacted_customers_total}</span></p>
                        ) : null}
                        <p>Recent event: <span className="text-white">{selectedRecentEvent?.title || '-'}</span></p>
                        <p>Probable cause: <span className="text-white">{selectedProbableCause}</span></p>
                        {customerImpactSummary.mitigation.hint ? (
                          <p>Mitigasi: <span className="text-cyan-200">{customerImpactSummary.mitigation.hint}</span></p>
                        ) : null}
                        {selectedEntity.type === 'nas' ? (
                          <p>
                            Router relations:{' '}
                            <span className="text-white">
                              {routerRelationsViewState === 'success'
                                ? `${activeRouterRelations.length} active${selectedRouterTelemetryHint ? ` | ${selectedRouterTelemetryHint}` : ''}`
                                : routerRelationsViewState === 'empty'
                                  ? 'Belum ada relasi router'
                                  : routerRelationsViewState === 'loading'
                                    ? 'Memuat...'
                                    : '-'}
                            </span>
                          </p>
                        ) : null}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <UIButton
                          variant="subtle"
                          as="link"
                          to={selectedPrimaryIncident
                            ? `/recovery?incidentId=${encodeURIComponent(String(selectedPrimaryIncident.id || ''))}`
                            : '/recovery'}
                          onClick={() => {
                            const label = selectedPrimaryIncident?.id
                              ? `Recovery dibuka dari topology untuk incident ${selectedPrimaryIncident.id}.`
                              : 'Recovery dibuka dari topology.';
                            void createExecutionEntry({ actionType: 'recovery_opened', message: label });
                          }}
                        >
                          Open in Recovery
                        </UIButton>
                        <UIButton variant="ghost" onClick={() => setIsDetailPanelOpen(true)}>Pin Detail</UIButton>
                      </div>
                    </div>
                  </div>
                )}
                {mode === 'live' && isNodeInvestigationState ? (
                  <div className="pointer-events-none absolute bottom-3 left-3 z-[600]">
                    <div className="rounded-md border border-white/20 bg-[#081223]/80 px-2.5 py-1.5 text-[11px] text-slate-200 backdrop-blur-sm">
                      {selectedEntity.name} | {String(selectedEntity.type || '').toUpperCase()}
                    </div>
                  </div>
                ) : null}
              </div>

              {mode === 'live' ? (
                <div className="grid gap-2 md:grid-cols-3">
                  <div className="rounded-lg border border-white/10 bg-[#091224] p-3"><p className="text-xs text-slate-400">Impacted Customers</p><p className="mt-1 text-xl font-semibold text-rose-300">{keyStats.degradedCustomers}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#091224] p-3"><p className="text-xs text-slate-400">Active Incidents</p><p className="mt-1 text-xl font-semibold text-amber-300">{keyStats.incidents}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#091224] p-3"><p className="text-xs text-slate-400">Operational Risk Nodes</p><p className="mt-1 text-xl font-semibold text-cyan-300">{keyStats.highRiskOdps}</p></div>
                </div>
              ) : (
                <div className="grid gap-2 md:grid-cols-4">
                  <div className="rounded-lg border border-white/10 bg-[#091224] p-3"><p className="text-xs text-slate-400">Planning Priorities</p><p className="mt-1 text-xl font-semibold text-amber-300">{keyStats.recommendations}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#091224] p-3"><p className="text-xs text-slate-400">Capacity-Critical Entities</p><p className="mt-1 text-xl font-semibold text-rose-300">{keyStats.highRiskOdps}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#091224] p-3"><p className="text-xs text-slate-400">Readiness Issues</p><p className="mt-1 text-xl font-semibold text-cyan-300">{keyStats.planReadinessIssues}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#091224] p-3"><p className="text-xs text-slate-400">Active Suggestions</p><p className="mt-1 text-xl font-semibold text-emerald-300">{keyStats.recommendations}</p></div>
                </div>
              )}
            </SectionContainer>

            {showTimeline && (
              <SectionContainer className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Timeline / Event Panel</p>
                    <StatusBadge tone="neutral">{timelineEvents.length} EVENTS</StatusBadge>
                  </div>
                  <button type="button" onClick={() => setTimelineExpanded((prev) => !prev)} className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-xs text-slate-300">
                    {timelineExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {timelineExpanded ? 'Ringkas' : 'Perluas'}
                  </button>
                </div>
                {timelineEvents.length === 0 ? <p className="text-xs text-slate-400">Belum ada event pada scope ini.</p> : (
                  <div className={`${timelineMaxHeightClass} space-y-1.5 overflow-auto pr-1`}>
                    {timelineEvents.map((event) => (
                      <div key={event.id} className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2">
                        <div className="mb-1 flex items-center justify-between gap-2"><p className="text-sm font-semibold text-white">{event.title}</p><StatusBadge tone={event.tone}>{event.timestamp}</StatusBadge></div>
                        <p className="text-xs text-slate-400">{event.detail}</p>
                      </div>
                    ))}
                  </div>
                )}
              </SectionContainer>
            )}
          </div>

          {isDetailPanelOpen && (mode === 'plan' || selectedEntity) && (
          <SectionContainer className="space-y-4">
            <div>
              <p className="cc-text-subtle text-[11px] uppercase tracking-[0.14em]">Detail Panel</p>
              <p className="cc-text-secondary mt-1 text-xs">{mode === 'live' ? (selectedPrimaryIncident ? 'Context ready' : 'Relation incomplete') : 'Planning context ready'}</p>
            </div>

            {!selectedEntity ? (
              <div className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-4 text-sm text-slate-400">Pilih entitas dari rail kiri atau marker di map.</div>
            ) : (
              <>
                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-start justify-between gap-2"><div><p className="text-sm font-semibold text-white">{selectedEntity.name}</p><p className="text-xs text-slate-400">{selectedEntity.subtitle}</p></div><StatusBadge tone={resolveStatusTone(selectedEntity.status)}>{String(selectedEntity.status || 'unknown').toUpperCase()}</StatusBadge></div>
                  <div className="space-y-1.5 text-xs text-slate-300">
                    <p>Entity: <span className="text-white">{selectedEntity.type.toUpperCase()}</span></p>
                    {selectedEntity.type === 'customer' && (<><p>ODP: <span className="text-white">{selectedEntity.data?.linked_odp_id || selectedEntity.data?.odp_id || '-'}</span></p><p>OLT: <span className="text-white">{selectedEntity.data?.linked_olt_id || selectedEntity.data?.olt_id || '-'}</span></p><p>Router/NAS: <span className="text-white">{selectedEntity.data?.router_id || '-'}</span></p></>)}
                    {selectedEntity.type === 'odp' && (<><p>OLT: <span className="text-white">{selectedEntity.data?.olt_id || '-'}</span></p><p>Kriteria: <span className="text-white">{normalizeKriteria(selectedEntity.data?.kriteria).toUpperCase()}</span></p></>)}
                    {selectedEntity.type === 'nas' && (
                      <>
                        <p>Site: <span className="text-white">{selectedEntity.data?.site_name || selectedEntity.data?.site || selectedEntity.siteId || '-'}</span></p>
                        <p>Identifier: <span className="text-white">{selectedEntity.data?.nas_identifier || selectedEntity.id || '-'}</span></p>
                        <p>Router Role: <span className="text-white">{selectedEntity.routerRole?.label || 'Distribution'}</span> <span className="text-slate-500">({selectedEntity.routerRole?.source || 'derived'})</span></p>
                      </>
                    )}
                    {!Array.isArray(selectedEntity.coordinates) ? <p className="text-slate-500">Koordinat map belum tersedia atau tidak valid operasional untuk entitas ini.</p> : null}
                  </div>
                </div>

                {selectedEntity.type === 'nas' ? (
                  <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs uppercase tracking-wide text-slate-400">Router Relations</p>
                      <UIButton
                        variant="ghost"
                        as="link"
                        to={`/infrastructure?nodeType=nas&nodeId=${encodeURIComponent(String(selectedEntity.id || ''))}&relationFocus=1`}
                      >
                        Open in Infrastructure
                      </UIButton>
                    </div>
                    {routerRelationsViewState === 'unavailable' ? (
                      <p className="text-xs text-slate-400">{selectedRouterContext.reason || 'Relasi router tidak tersedia untuk node ini.'}</p>
                    ) : null}
                    {routerRelationsViewState === 'loading' ? (
                      <p className="text-xs text-slate-400">Masih memuat data...</p>
                    ) : null}
                    {routerRelationsViewState === 'error' ? (
                      <p className="text-xs text-rose-200">{routerRelationsError}</p>
                    ) : null}
                    {routerRelationsViewState === 'empty' ? (
                      <p className="text-xs text-slate-400">Belum ada relasi router.</p>
                    ) : null}
                    {routerRelationsViewState === 'success' ? (
                      <div className="space-y-1.5">
                        {activeRouterRelations.slice(0, 6).map((relation) => {
                          const fromId = String(relation?.from_router_id || '').trim();
                          const toId = String(relation?.to_router_id || '').trim();
                          const fromMeta = routerOptionById.get(fromId);
                          const toMeta = routerOptionById.get(toId);
                          const relationType = String(relation?.relation_type || 'primary').trim().toLowerCase();
                          const relationLabel = RELATION_TYPE_LABELS[relationType] || relationType || 'primary';
                          const fromName = relation?.from_router_name || fromMeta?.name || fromId || '-';
                          const toName = relation?.to_router_name || toMeta?.name || toId || '-';
                          const fromRole = normalizeRouterRoleToken(fromMeta?.role || relation?.from_router_role);
                          const toRole = normalizeRouterRoleToken(toMeta?.role || relation?.to_router_role);
                          return (
                            <div key={String(relation?.id || `${fromId}-${toId}-${relationType}`)} className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2">
                              <p className="text-[11px] text-slate-100">
                                {fromName} ({fromRole}) -> {toName} ({toRole})
                              </p>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <StatusBadge tone={relationType === 'backup' ? 'warn' : relationType === 'balancing' ? 'info' : 'ok'}>{relationLabel}</StatusBadge>
                                <StatusBadge tone={relation?.is_active ? 'ok' : 'neutral'}>{relation?.is_active ? 'ACTIVE' : 'INACTIVE'}</StatusBadge>
                                <StatusBadge tone={resolveRelationTelemetryTone(relation?.link_status)}>{String(relation?.link_status || 'unknown').toUpperCase()}</StatusBadge>
                                {relation?.priority_order != null ? <span className="text-[10px] text-slate-400">Priority {relation.priority_order}</span> : null}
                              </div>
                              <p className="mt-1 text-[10px] text-slate-400">
                                Usage: <span className="text-slate-200">{String(relation?.path_usage || 'unknown').replaceAll('_', ' ')}</span>
                                {' | '}
                                Traffic: <span className="text-slate-200">{relation?.traffic_hint || 'Traffic hint tidak tersedia'}</span>
                                {' | '}
                                Last seen: <span className="text-slate-200">{relation?.last_seen ? new Date(relation.last_seen).toLocaleString('id-ID') : '-'}</span>
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Related Tickets</p>
                    <StatusBadge tone="neutral">{activeRelatedTickets.length} ACTIVE</StatusBadge>
                  </div>
                  {selectedTicketContext ? (
                    <p className="mb-2 text-[11px] text-slate-400">
                      Context: {selectedTicketContext.sourceType}:{selectedTicketContext.sourceId || '-'}
                      {selectedTicketContext.siteId ? <> | site {selectedTicketContext.siteId}</> : null}
                      {selectedTicketContext.customerId ? <> | customer {selectedTicketContext.customerId}</> : null}
                    </p>
                  ) : null}
                  {ticketsLoading ? <p className="text-xs text-slate-400">Memuat ticket terkait...</p> : null}
                  {ticketsError ? <p className="text-xs text-rose-200">{ticketsError}</p> : null}
                  {!ticketsLoading && !ticketsError && relatedTickets.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada ticket terkait. Buat ticket untuk mulai melacak penanganan.</p>
                  ) : null}
                  {!ticketsLoading && !ticketsError && relatedTickets.length > 0 ? (
                    <div className="space-y-1.5">
                      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-slate-400">
                        <span>Terbaru: <span className="text-slate-100">{latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}</span></span>
                        <span>Total: <span className="text-slate-100">{relatedTickets.length}</span></span>
                      </div>
                      {(showAllRelatedTickets ? relatedTickets : relatedTickets.slice(0, 3)).map((ticket) => (
                        <div key={ticket.id} className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">{ticket.ticketCode || ticket.id}</p>
                            <StatusBadge tone={ticketStatusTone(ticket.status)}>
                              {String(ticket.status || 'open').toLowerCase()}
                            </StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{ticket.title || '-'}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <StatusBadge tone={ticketPriorityTone(ticket.priority)}>{String(ticket.priority || 'medium').toLowerCase()}</StatusBadge>
                            <span className="text-[10px] text-slate-500">PIC: {ticket.assignedTo || '-'}</span>
                            <span className="text-[10px] text-slate-500">Update: {ticket.updated_at ? new Date(ticket.updated_at).toLocaleString('id-ID') : '-'}</span>
                          </div>
                        </div>
                      ))}
                      {relatedTickets.length > 3 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllRelatedTickets((prev) => !prev)}
                        >
                          {showAllRelatedTickets ? 'Tampilkan ringkas' : 'Tampilkan lebih banyak'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-2 space-y-2 rounded-md border border-white/10 bg-[#101b2e] p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">Buat Ticket</p>
                    <input
                      value={ticketDraft.title}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder={`Issue ${selectedEntity.type.toUpperCase()} ${selectedEntity.name || selectedEntity.id}`}
                      className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <textarea
                      value={ticketDraft.description}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, description: event.target.value }))}
                      rows={2}
                      placeholder="Tulis deskripsi issue dan dampak operasional"
                      className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={ticketDraft.priority}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, priority: event.target.value }))}
                        className="rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-200"
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                        <option value="critical">critical</option>
                      </select>
                      <input
                        value={ticketDraft.assigned_to}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, assigned_to: event.target.value }))}
                        placeholder="PIC / owner"
                        className="rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                    <UIButton variant="subtle" onClick={handleCreateTicket} disabled={ticketSaving}>
                      Buat Ticket
                    </UIButton>
                    {activeRelatedTickets.length > 0 ? (
                      <p className="text-[11px] text-amber-300">
                        Sudah ada {activeRelatedTickets.length} ticket aktif untuk source ini. Ticket aktif terbaru: {latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}.
                        Anda tetap bisa membuat ticket baru jika memang perlu.
                      </p>
                    ) : null}
                    {relatedTickets[0] ? (
                      <div className="space-y-2">
                        {(showAllTicketLogs ? (relatedTickets[0].actionLog || []).slice().reverse() : (relatedTickets[0].actionLog || []).slice(-3).reverse()).map((entry) => (
                          <p key={entry.id || `${entry.created_at}-${entry.message}`} className="text-[11px] text-slate-300">
                            {entry.message} <span className="text-slate-500">({entry.actor || '-'} | {entry.created_at ? new Date(entry.created_at).toLocaleString('id-ID') : '-'})</span>
                          </p>
                        ))}
                        {(relatedTickets[0].actionLog || []).length > 3 ? (
                          <button
                            type="button"
                            className="text-[11px] text-cyan-300 hover:text-cyan-200"
                            onClick={() => setShowAllTicketLogs((prev) => !prev)}
                          >
                            {showAllTicketLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua log'}
                          </button>
                        ) : null}
                        <div className="flex gap-2">
                          <input
                            value={ticketUpdateNote}
                            onChange={(event) => setTicketUpdateNote(event.target.value)}
                            placeholder="Tulis update investigasi atau tindakan yang dilakukan"
                            className="flex-1 rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                          />
                          <UIButton variant="ghost" onClick={() => handleAddTicketUpdate(relatedTickets[0].id)} disabled={ticketSaving}>
                            Simpan Update
                          </UIButton>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Runbook Preset</p>
                  <div className="flex flex-wrap gap-1.5">
                    {RUNBOOK_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleRunbookPreset(preset)}
                        disabled={ticketSaving}
                        className="rounded-md border border-white/15 bg-[#0f1a2b] px-2 py-1 text-[11px] text-slate-200 hover:border-cyan-400/60 hover:text-cyan-200 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] text-slate-500">Mencatat langkah operasional context entity yang sedang dipilih.</p>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Last Operational Update</p>
                  {latestExecutionLog ? (
                    <div className="space-y-1 text-xs text-slate-300">
                      <p className="font-semibold text-white">{getExecutionActionLabel(latestExecutionLog.actionType)}</p>
                      <p>{latestExecutionLog.message || '-'}</p>
                      <p className="text-[11px] text-slate-500">
                        {latestExecutionLog.actor || '-'} | {latestExecutionLog.createdAt ? new Date(latestExecutionLog.createdAt).toLocaleString('id-ID') : '-'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Belum ada tindakan tercatat.</p>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Execution Log</p>
                    <span className="text-[10px] text-slate-500">Total {executionLogs.length}</span>
                  </div>
                  {executionLogsLoading ? <p className="text-xs text-slate-400">Masih memuat data...</p> : null}
                  {!executionLogsLoading && executionLogsError ? <p className="text-xs text-rose-300">{executionLogsError}</p> : null}
                  {!executionLogsLoading && !executionLogsError && executionLogs.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada data terkait. Langkah operasional akan muncul di sini.</p>
                  ) : null}
                  {!executionLogsLoading && !executionLogsError && executionLogs.length > 0 ? (
                    <div className="space-y-1.5">
                      {visibleExecutionLogs.map((entry) => (
                        <div key={entry.id || `${entry.createdAt}-${entry.message}`} className="rounded-md border border-white/10 bg-[#101b2e] px-2 py-1.5">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold text-slate-100">{getExecutionActionLabel(entry.actionType)}</p>
                            <StatusBadge tone={executionActionTone(entry.actionType)}>{entry.actor || '-'}</StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{entry.message}</p>
                          <p className="mt-1 text-[10px] text-slate-500">{entry.createdAt ? new Date(entry.createdAt).toLocaleString('id-ID') : '-'}</p>
                        </div>
                      ))}
                      {executionLogs.length > 5 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllExecutionLogs((prev) => !prev)}
                        >
                          {showAllExecutionLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-2 flex gap-2">
                    <input
                      value={executionNote}
                      onChange={(event) => setExecutionNote(event.target.value)}
                      placeholder="Tambah catatan runbook operasional"
                      className="flex-1 rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <UIButton variant="ghost" onClick={handleAddExecutionNote} disabled={ticketSaving}>Simpan Note</UIButton>
                  </div>
                </div>

                {mode === 'live' ? (
                  <>
                    <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs uppercase tracking-wide text-slate-400">Customer Impact</p>
                        <StatusBadge tone={customerImpactSummary.severityTone}>{String(customerImpactSummary.severityLabel || 'low').toUpperCase()}</StatusBadge>
                      </div>
                      {customerImpactSummary.state !== 'ok' ? (
                        <p className="text-xs text-slate-400">{customerImpactSummary.message}</p>
                      ) : (
                        <div className="space-y-1.5 text-xs text-slate-300">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <StatusBadge tone="neutral">Impacted {customerImpactSummary.counts.total}</StatusBadge>
                            <StatusBadge tone={customerImpactSummary.counts.offline > 0 ? 'danger' : 'neutral'}>Offline {customerImpactSummary.counts.offline}</StatusBadge>
                            <StatusBadge tone={customerImpactSummary.counts.degraded > 0 ? 'warn' : 'neutral'}>Degraded {customerImpactSummary.counts.degraded}</StatusBadge>
                            <StatusBadge tone={customerImpactSummary.counts.warning > 0 ? 'info' : 'neutral'}>Warning {customerImpactSummary.counts.warning}</StatusBadge>
                          </div>
                          <p>Related incident: <span className="text-white">{selectedPrimaryIncident ? selectedPrimaryIncident.id : 'No direct incident linked'}</span></p>
                          <p>Recent event: <span className="text-white">{selectedRecentEvent?.title || 'Relation incomplete'}</span></p>
                          <p>Probable cause: <span className="text-white">{selectedProbableCause || 'Relation incomplete'}</span></p>
                          {customerImpactSummary.sites.length > 0 ? (
                            <p>Sites impacted: <span className="text-white">{customerImpactSummary.sites.slice(0, 2).map((item) => `${item.site} (${item.total})`).join(' | ')}</span></p>
                          ) : (
                            <p className="text-slate-500">Site/customer segment impact belum lengkap.</p>
                          )}
                          {customerImpactSummary.topCustomers.length > 0 ? (
                            <div className="space-y-1">
                              {customerImpactSummary.topCustomers.slice(0, 3).map((customer) => (
                                <p key={customer.id} className="rounded-md border border-white/10 bg-[#101b2e] px-2 py-1.5">
                                  {customer.name} ({customer.code}) | {String(customer.state || 'unknown').toUpperCase()}
                                </p>
                              ))}
                          {customerImpactSummary.counts.total > 3 ? (
                                <button
                                  type="button"
                                  onClick={() => setShowImpactDrawer(true)}
                                  className="text-[11px] text-cyan-300 hover:text-cyan-200"
                                >
                                  +{customerImpactSummary.counts.total - 3} lainnya | Lihat semua
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                          {customerImpactSummary.mitigation.hint ? (
                            <p className="text-cyan-200">{customerImpactSummary.mitigation.hint}</p>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs uppercase tracking-wide text-slate-400">Site Impact</p>
                        <StatusBadge tone={siteImpactSummary.selectedSite?.severityTone || 'neutral'}>
                          {String(siteImpactSummary.selectedSite?.severity || 'low').toUpperCase()}
                        </StatusBadge>
                      </div>
                      {siteImpactSummary.state !== 'ok' || (siteImpactSummary.topSites || []).length === 0 ? (
                        <p className="text-xs text-slate-400">{siteImpactSummary.message || 'Belum ada dampak site yang terdeteksi.'}</p>
                      ) : (
                        <div className="space-y-1.5 text-xs text-slate-300">
                          {siteImpactSummary.topSites.slice(0, 3).map((site) => (
                            <div key={site.site_id} className="rounded-md border border-white/10 bg-[#101b2e] px-2 py-1.5">
                              <div className="mb-1 flex items-center justify-between gap-2">
                                <p className="font-semibold text-white">{site.site_name || site.site_id}</p>
                                <StatusBadge tone={site.severityTone}>{String(site.severity || 'low').toUpperCase()}</StatusBadge>
                              </div>
                              <p>
                                Impacted {site.impacted_customers_total}
                                {' | '}
                                Offline {site.offline_count}
                                {' | '}
                                Degraded {site.degraded_count}
                                {' | '}
                                Incident {site.active_incident_count}
                                {' | '}
                                Ticket {site.active_ticket_count}
                              </p>
                              <p className="mt-1 text-slate-400">
                                Node terkait: {site.top_related_nodes?.slice(0, 2).map((node) => node.name || node.id).join(' | ') || 'Belum ada node terkait'}
                              </p>
                              <p className="mt-1 text-slate-400">
                                Last operational: {site.last_operational_update
                                  ? `${getExecutionActionLabel(site.last_operational_update.actionType)} | ${site.last_operational_update.createdAt ? new Date(site.last_operational_update.createdAt).toLocaleString('id-ID') : '-'}`
                                  : 'Belum ada tindakan tercatat'}
                              </p>
                              {site.mitigation_hint ? <p className="mt-1 text-cyan-200">{site.mitigation_hint}</p> : null}
                            </div>
                          ))}
                          <div className="flex flex-wrap gap-2">
                            <UIButton
                              variant="ghost"
                              as="link"
                              to={siteImpactSummary.selectedSite?.site_id ? `/recovery?siteId=${encodeURIComponent(String(siteImpactSummary.selectedSite.site_id || ''))}` : '/recovery'}
                            >
                              Open in Recovery
                            </UIButton>
                            <UIButton
                              variant="ghost"
                              as="link"
                              to={siteImpactSummary.selectedSite?.site_id ? `/infrastructure?tab=sites&siteId=${encodeURIComponent(String(siteImpactSummary.selectedSite.site_id || ''))}` : '/infrastructure?tab=sites'}
                            >
                              Open in Infrastructure
                            </UIButton>
                            <UIButton
                              variant="ghost"
                              as="link"
                              to={siteImpactSummary.selectedSite?.site_id ? `/tickets?siteId=${encodeURIComponent(String(siteImpactSummary.selectedSite.site_id || ''))}` : '/tickets'}
                            >
                              Open in Tickets
                            </UIButton>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                      <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Actions</p>
                      <div className="grid gap-2">
                        <UIButton
                          variant="subtle"
                          as="link"
                          to={selectedPrimaryIncident
                            ? `/recovery?incidentId=${encodeURIComponent(String(selectedPrimaryIncident.id || ''))}`
                            : '/recovery'}
                        >
                          Open in Recovery
                        </UIButton>
                        <UIButton
                          variant="ghost"
                          as="link"
                          to={selectedCustomerId
                            ? `/customers?customerId=${encodeURIComponent(selectedCustomerId)}`
                            : '/customers'}
                        >
                          View Customer
                        </UIButton>
                        <UIButton
                          variant="ghost"
                          as="link"
                          to={selectedInfraRef
                            ? `/infrastructure?nodeType=${encodeURIComponent(selectedInfraRef.type)}&nodeId=${encodeURIComponent(selectedInfraRef.id)}`
                            : '/infrastructure'}
                        >
                          Open in Infrastructure
                        </UIButton>
                        {selectedEntity?.type === 'nas' ? (
                          <UIButton
                            variant="ghost"
                            as="link"
                            to={`/infrastructure?nodeType=nas&nodeId=${encodeURIComponent(String(selectedEntity.id || ''))}&relationFocus=1`}
                          >
                            View Router Relations
                          </UIButton>
                        ) : null}
                      </div>
                    </div>

                    <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                      <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Metadata</p>
                      <div className="space-y-1.5 text-xs text-slate-300">
                        <p>Status: <span className="text-white">{selectedPrimaryIncident ? 'Context ready' : 'Recovery unavailable'}</span></p>
                        <p>Last update: <span className="text-white">{lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</span></p>
                        <p>Mode: <span className="text-white">Live Monitor</span></p>
                        <p>Scope: <span className="text-white">{`Infra ${infraScope.toUpperCase()} | Site ${siteFilter === 'all' ? 'ALL' : siteFilter}`}</span></p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                      {planDecisionSupport && (
                        <div className="mb-3 space-y-2 rounded-md border border-white/10 bg-[#101b2e] p-2.5">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400">Capacity &amp; Readiness</p>
                          <p className="text-xs text-slate-300">Utilisasi: <span className="text-white">{planDecisionSupport.utilization}</span></p>
                          <p className="text-xs text-slate-300">Threshold: <span className="text-white">{planDecisionSupport.threshold}</span></p>
                          <p className="text-xs text-slate-300">Readiness: <span className="text-white">{planDecisionSupport.readiness}</span></p>
                          <p className="text-xs text-slate-300">Nearest infra: <span className="text-white">{planDecisionSupport.nearestHint}</span></p>
                          <p className="text-xs text-slate-300">Demand note: <span className="text-white">{planDecisionSupport.demandNote}</span></p>
                        </div>
                      )}
                      <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Planning Suggestions</p>
                      {selectedRecommendations.length === 0 ? <p className="text-xs text-slate-400">Belum ada planning suggestion untuk entitas ini pada scope aktif.</p> : (
                        <div className="space-y-2">
                          {selectedRecommendations.map((item) => (
                            <div key={item.id} className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2">
                              <div className="mb-1 flex items-center justify-between"><p className="text-sm font-medium text-white">{item.title}</p><StatusBadge tone={item.urgency?.tone === 'critical' ? 'danger' : item.urgency?.tone === 'high' ? 'warn' : 'info'}>{item.urgency?.label || 'Info'}</StatusBadge></div>
                              <p className="text-xs text-slate-400">{item.summary}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                      <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Planning Notes</p>
                      <div className="space-y-1.5 text-xs text-slate-300">
                        {capacityPrediction.insights.length
                          ? capacityPrediction.insights.map((insight, idx) => <p key={`insight-${idx}`} className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2">{insight}</p>)
                          : <p className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2 text-slate-400">Belum ada planning insight pada scope aktif.</p>}
                      </div>
                      {planDecisionSupport && (
                        <div className="mt-3 rounded-md border border-white/10 bg-[#101b2e] p-2.5 text-xs text-slate-300">
                          <p className="font-semibold text-white">Next Planning Step</p>
                          <p className="mt-1">{planDecisionSupport.nextStep}</p>
                          <p className="mt-1 text-slate-400">{planDecisionSupport.planningNote}</p>
                          <p className="mt-1 text-amber-300">Risk if no action: {planDecisionSupport.riskIfNoAction}</p>
                        </div>
                      )}
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <UIButton variant="subtle">Draft Expansion</UIButton>
                        <UIButton variant="ghost">Assign Survey</UIButton>
                        <UIButton variant="ghost" as="link" to={infrastructureCreateOltLink}>Tambah OLT</UIButton>
                        <UIButton variant="ghost" as="link" to={infrastructureCreateOdpLink}>Tambah ODP</UIButton>
                      </div>
                      <div className="mt-3 grid gap-2">
                        <UIButton
                          variant="subtle"
                          as="link"
                          to={infrastructureEditLink}
                        >
                          {selectedEntity?.type === 'olt' ? 'Edit OLT' : selectedEntity?.type === 'odp' ? 'Edit ODP' : 'Open in Infrastructure'}
                        </UIButton>
                        <UIButton variant="ghost" as="link" to="/ai-console">Open AI Console</UIButton>
                      </div>
                    </div>
                  </>
                )}

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3 text-xs text-slate-400">
                  <p>Last update: <span className="text-slate-200">{lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</span></p>
                  <p className="mt-1">Mode: <span className="text-slate-200">{mode === 'live' ? 'Live Monitor' : 'Plan / Design'}</span></p>
                  <p className="mt-1">NAS terdata: <span className="text-slate-200">{nasRouters.length}</span></p>
                </div>
              </>
            )}
          </SectionContainer>
          )}
        </div>
      </PageContainer>
      <CustomerImpactDrawer
        open={showImpactDrawer}
        onClose={() => setShowImpactDrawer(false)}
        title="Semua Customer Terdampak"
        subtitle={selectedEntity ? `Context ${String(selectedEntity.type || '').toUpperCase()} ${selectedEntity.name || selectedEntity.id || ''}` : ''}
        customers={customerImpactSummary.customers || []}
        recoveryIncidentId={selectedPrimaryIncident?.id || ''}
      />
    </AppShell>
  );
}

export default TopologyWorkspaceUnified;


